/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetMarketsRow, GetOfficialMarketsRow, GetCreatedMarketsAndProposalsRow, GetMarketRecapRow, GetMarketRow, GetMarketTradersRow, GetMarketBackersRow, GetMarketChartRow, GetMarketConvictionChartRow, SimilarMarketsByMarketRow, SimilarWinnersByMarketRow, GetMarketBoostsRow, GetMarketBoostEventsRow, GetMarketBoosterLeaderboardRow, GetMarketChainMetadataRow, GetOutcomeSuggestionsRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String, HELPERS } from '../../helpers/index.js';
import { fromBinary } from '@bufbuild/protobuf';
import { decodeRealtimePayload } from '../../devtool/decode-realtime-payload.js';
import { AmmUserActivitySchema, BackerFeeClaimEventSchema, MarketBackingSchema, MarketBoostSchema, MarketCreatedNotificationSchema, MarketLifecycleSchema, MarketPrizeClaimSchema, MarketResolvedSchema, MarketTradeSchema, OutcomeAddedSchema, OutcomeSuggestionEventSchema, OutcomeSuggestionVoteUpdateSchema, ProposalVoteUpdateSchema } from '@morfi/proto';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import type { PagedData } from '../../types/PagedData.js';

import { readResponseSubjectIds } from '../../core/internal/response-subjects.js';


// ─── live re-rank · shared comparators ─────────────────────────────────────

function _toBigIntOrZero(v: unknown): bigint {
  if (typeof v === 'bigint') return v;
  if (typeof v === 'number') return Number.isFinite(v) ? BigInt(Math.trunc(v)) : 0n;
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === '' || !/^-?\d+$/.test(t)) return 0n;
    try {
      return BigInt(t);
    } catch {
      return 0n;
    }
  }
  return 0n;
}

function _cmpD6(a: unknown, b: unknown): number {
  const av = _toBigIntOrZero(a);
  const bv = _toBigIntOrZero(b);
  return av < bv ? -1 : av > bv ? 1 : 0;
}

function _cmpNum(a: unknown, b: unknown): number {
  const av = typeof a === 'number' ? a : Number(a);
  const bv = typeof b === 'number' ? b : Number(b);
  const an = Number.isFinite(av) ? av : 0;
  const bn = Number.isFinite(bv) ? bv : 0;
  return an < bn ? -1 : an > bn ? 1 : 0;
}

function _cmpStr(a: unknown, b: unknown): number {
  const av = a == null ? '' : String(a);
  const bv = b == null ? '' : String(b);
  return av < bv ? -1 : av > bv ? 1 : 0;
}


// ─── useGetMarkets ────────────────────────────────────────────────────────────

export interface useGetMarketsArgs { readonly marketType?: string; readonly classification?: string; readonly status?: string; readonly statusNot?: string; readonly category?: string; readonly location?: string; readonly search?: string; readonly creator?: string; readonly creatorUserId?: string; readonly pubkeys?: string; readonly volumeMin?: string; readonly volumeMax?: string; readonly createdAfter?: string; readonly createdBefore?: string; readonly outcomesMin?: string; readonly outcomesMax?: string; readonly prizePoolMin?: string; readonly prizePoolMax?: string; readonly liquidityMin?: string; readonly liquidityMax?: string; readonly backersMin?: string; readonly backersMax?: string; readonly tradersMin?: string; readonly tradersMax?: string; readonly sortBy?: string; readonly sortOrder?: string; readonly viewportGated?: boolean; }


async function _useGetMarketsQueryFn(
  args: useGetMarketsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetMarketsRow>> {
  const url = '/v1/markets';
  const params = new URLSearchParams();

  if (args.marketType !== undefined) params.set('marketType', String(args.marketType));
  if (args.classification !== undefined) params.set('classification', String(args.classification));
  if (args.status !== undefined) params.set('status', String(args.status));
  if (args.statusNot !== undefined) params.set('statusNot', String(args.statusNot));
  if (args.category !== undefined) params.set('category', String(args.category));
  if (args.location !== undefined) params.set('location', String(args.location));
  if (args.search !== undefined) params.set('search', String(args.search));
  if (args.creator !== undefined) params.set('creator', String(args.creator));
  if (args.creatorUserId !== undefined) params.set('creatorUserId', String(args.creatorUserId));
  if (args.pubkeys !== undefined) params.set('pubkeys', String(args.pubkeys));
  if (args.volumeMin !== undefined) params.set('volumeMin', String(args.volumeMin));
  if (args.volumeMax !== undefined) params.set('volumeMax', String(args.volumeMax));
  if (args.createdAfter !== undefined) params.set('createdAfter', String(args.createdAfter));
  if (args.createdBefore !== undefined) params.set('createdBefore', String(args.createdBefore));
  if (args.outcomesMin !== undefined) params.set('outcomesMin', String(args.outcomesMin));
  if (args.outcomesMax !== undefined) params.set('outcomesMax', String(args.outcomesMax));
  if (args.prizePoolMin !== undefined) params.set('prizePoolMin', String(args.prizePoolMin));
  if (args.prizePoolMax !== undefined) params.set('prizePoolMax', String(args.prizePoolMax));
  if (args.liquidityMin !== undefined) params.set('liquidityMin', String(args.liquidityMin));
  if (args.liquidityMax !== undefined) params.set('liquidityMax', String(args.liquidityMax));
  if (args.backersMin !== undefined) params.set('backersMin', String(args.backersMin));
  if (args.backersMax !== undefined) params.set('backersMax', String(args.backersMax));
  if (args.tradersMin !== undefined) params.set('tradersMin', String(args.tradersMin));
  if (args.tradersMax !== undefined) params.set('tradersMax', String(args.tradersMax));
  if (args.sortBy !== undefined) params.set('sortBy', String(args.sortBy));
  if (args.sortOrder !== undefined) params.set('sortOrder', String(args.sortOrder));
  params.set('limit', String(limitOverride ?? 24));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetMarkets',
    routeTemplate: '/v1/markets',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetMarketsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetMarketsFactory = Object.assign(
  (args: useGetMarketsArgs) => ({
    queryKey: ['markets', 'list', { marketType: args.marketType ?? null, classification: args.classification ?? null, status: args.status ?? null, statusNot: args.statusNot ?? null, category: args.category ?? null, location: args.location ?? null, search: args.search ?? null, creator: args.creator ?? null, creatorUserId: args.creatorUserId ?? null, pubkeys: args.pubkeys ?? null, volumeMin: args.volumeMin ?? null, volumeMax: args.volumeMax ?? null, createdAfter: args.createdAfter ?? null, createdBefore: args.createdBefore ?? null, outcomesMin: args.outcomesMin ?? null, outcomesMax: args.outcomesMax ?? null, prizePoolMin: args.prizePoolMin ?? null, prizePoolMax: args.prizePoolMax ?? null, liquidityMin: args.liquidityMin ?? null, liquidityMax: args.liquidityMax ?? null, backersMin: args.backersMin ?? null, backersMax: args.backersMax ?? null, tradersMin: args.tradersMin ?? null, tradersMax: args.tradersMax ?? null, sortBy: args.sortBy ?? null, sortOrder: args.sortOrder ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMarketsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetMarkets',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['markets', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMarketsArgs, PagedData<GetMarketsRow>>;


export const useGetMarketsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetMarketsFactory },
  parseInput: (raw: unknown): useGetMarketsArgs => raw as useGetMarketsArgs,
  parseResponse: (raw: unknown): PagedData<GetMarketsRow> => raw as PagedData<GetMarketsRow>,
  specName: 'useGetMarkets' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['markets', 'entity', id] as readonly unknown[],
  nestedEntityProjection: {"field":"outcomes","resource":"outcomes","idField":"mint","referenceFields":["mint","outcomeIndex"],"fieldMap":{"totalSupply":"indexedOutcomeMintSupplyD6"},"canonicalIdField":"pubkey","parentIdField":"pubkey","canonicalParentField":"market"} as const,
} as unknown as HookSpec<useGetMarketsArgs, PagedData<GetMarketsRow>>;
export const useGetMarketsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 24,
  maxPageSize: 100,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetMarketsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetMarketsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetMarketsPerEntitySubjects(args: useGetMarketsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = ['morfi.market.*.user.*.trade.v0', 'morfi.market.*.user.*.boost.v0', 'morfi.market.*.lifecycle.v0', 'morfi.market.*.resolution.v0', ...((args as Record<string, unknown>)['sortBy'] === 'createdAt' ? ['morfi.market.created.v0'] : []), ...((args as Record<string, unknown>)['sortBy'] === 'resolvedAt' ? ['morfi.market.*.resolution.v0'] : [])];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetMarketsRowSubjectTemplates(_args: useGetMarketsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = ['morfi.market.{pubkey}.user.*.trade.v0', 'morfi.market.{pubkey}.user.*.boost.v0', 'morfi.market.{pubkey}.lifecycle.v0', 'morfi.market.{pubkey}.resolution.v0'];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetMarketsIsTerminalEvent(
  ev: MorfiEvent<unknown>,
  args: useGetMarketsArgs,
): boolean {
  const subject = ev.subject;
  if (typeof subject !== 'string') return false;
  if ((args as Record<string, unknown>)['status'] === "trading" && /^morfi\.market\.([^.]+)\.resolution\.v0$/.test(subject)) {
    return true;
  }
  return false;
}

function _useGetMarketsApplyEvent(
  prev: PagedData<GetMarketsRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetMarketsArgs,
): PagedData<GetMarketsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // morfi.market.${m}.user.*.trade.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.trade\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { pubkey?: string }).pubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetMarketsRow;
      let updated: GetMarketsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
        {
          const _arr = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['postOutcomeConvictionPpb']) ?? ((raw as Record<string, unknown> | undefined)?.['postOutcomeConvictionPpb']));
          const _list = (updated as unknown as { outcomes?: ReadonlyArray<Record<string, unknown>> }).outcomes;
          if (Array.isArray(_arr) && Array.isArray(_list)) {
            updated = {
              ...updated,
              outcomes: _list.map((__o) => {
                const __idxRaw = __o['outcomeIndex'];
                const __idx = typeof __idxRaw === 'number' ? __idxRaw : Number(__idxRaw);
                return Number.isInteger(__idx) && __idx >= 0 && __idx < _arr.length && _arr[__idx] !== undefined
                  ? { ...__o, convictionAnchor: String(_arr[__idx]) }
                  : __o;
              }),
            } as GetMarketsRow;
          }
        }
        {
          const _arr = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['postOutcomeConvictionPpb']) ?? ((raw as Record<string, unknown> | undefined)?.['postOutcomeConvictionPpb']));
          const _list = (updated as unknown as { outcomes?: ReadonlyArray<Record<string, unknown>> }).outcomes;
          if (Array.isArray(_arr) && Array.isArray(_list)) {
            updated = {
              ...updated,
              outcomes: _list.map((__o) => {
                const __idxRaw = __o['outcomeIndex'];
                const __idx = typeof __idxRaw === 'number' ? __idxRaw : Number(__idxRaw);
                return Number.isInteger(__idx) && __idx >= 0 && __idx < _arr.length && _arr[__idx] !== undefined
                  ? { ...__o, conviction: String(_arr[__idx]) }
                  : __o;
              }),
            } as GetMarketsRow;
          }
        }
        {
          const _arr = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['postOutcomeConvictionTargetPpb']) ?? ((raw as Record<string, unknown> | undefined)?.['postOutcomeConvictionTargetPpb']));
          const _list = (updated as unknown as { outcomes?: ReadonlyArray<Record<string, unknown>> }).outcomes;
          if (Array.isArray(_arr) && Array.isArray(_list)) {
            updated = {
              ...updated,
              outcomes: _list.map((__o) => {
                const __idxRaw = __o['outcomeIndex'];
                const __idx = typeof __idxRaw === 'number' ? __idxRaw : Number(__idxRaw);
                return Number.isInteger(__idx) && __idx >= 0 && __idx < _arr.length && _arr[__idx] !== undefined
                  ? { ...__o, convictionTarget: String(_arr[__idx]) }
                  : __o;
              }),
            } as GetMarketsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['ts']) ?? ((raw as Record<string, unknown> | undefined)?.['ts']));
          if (_v !== undefined) {
            updated = { ...updated, convictionUpdatedAt: String(_v) } as GetMarketsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['volumeTotal'];
            updated = { ...updated, volumeTotal: addD6String(_prevField, _v) } as GetMarketsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['prizePoolContributionD6']) ?? ((raw as Record<string, unknown> | undefined)?.['prizePoolContributionD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['prizePool'];
            updated = { ...updated, prizePool: addD6String(_prevField, _v) } as GetMarketsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['baseFeeToBackersD6']) ?? ((raw as Record<string, unknown> | undefined)?.['baseFeeToBackersD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['backerRewards'];
            updated = { ...updated, backerRewards: addD6String(_prevField, _v) } as GetMarketsRow;
          }
        }
        updated = (HELPERS.applyBcActivityToMarket as unknown as (prev: GetMarketsRow, raw: ReturnType<typeof fromBinary<typeof MarketTradeSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
        updated = (HELPERS.applyBcTradeMarkToMarket as unknown as (prev: GetMarketsRow, raw: ReturnType<typeof fromBinary<typeof MarketTradeSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // morfi.market.${m}.user.*.boost.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.boost\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { pubkey?: string }).pubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetMarketsRow;
      let updated: GetMarketsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBoostSchema, _d)
            : fromBinary(MarketBoostSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBoostSchema>>;
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['prizePool'];
            updated = { ...updated, prizePool: addD6String(_prevField, _v) } as GetMarketsRow;
          }
        }
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // morfi.market.${m}.lifecycle.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.lifecycle\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { pubkey?: string }).pubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetMarketsRow;
      let updated: GetMarketsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketLifecycleSchema, _d)
            : fromBinary(MarketLifecycleSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>;
        updated = (HELPERS.mapLifecycleStatus as unknown as (prev: GetMarketsRow, raw: ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // morfi.market.${m}.resolution.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.resolution\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { pubkey?: string }).pubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetMarketsRow;
      let updated: GetMarketsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketResolvedSchema, _d)
            : fromBinary(MarketResolvedSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketResolvedSchema>>;
        updated = (HELPERS.mapResolvedStatus as unknown as (prev: GetMarketsRow, raw: ReturnType<typeof fromBinary<typeof MarketResolvedSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
      // removeRow gate — splice this row out of the cache when the
      // generated terminal-event predicate matches. The patch above still
      // ran (the row's transient
      // updated identity briefly lives in `updated` before the splice
      // — useful for animation hooks that want to see the new field
      // value before the row disappears). When the gate doesn't match,
      // fall through to the normal updated-row replacement.
      {
        if (_useGetMarketsIsTerminalEvent(ev, args)) {
          const nextRows = prevRows.slice();
          nextRows.splice(_idx, 1);
          if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
          return { ...prev, data: nextRows, _normalizedPatchedEntity: updated } as PagedData<GetMarketsRow>;
        }
      }
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

function _useGetMarketsOnEvent(
  args: useGetMarketsArgs,
  queryClient: { getQueryData: (k: readonly unknown[]) => unknown; setQueryData: (k: readonly unknown[], updater: (prev: unknown) => unknown) => void },
): (ev: MorfiEvent<unknown>) => void {
  const p0 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  const p1 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  return (ev: MorfiEvent<unknown>): void => {
    const subject = ev.subject;
    if (typeof subject !== 'string') return;
    // Each prepend branch decodes its own row var with ITS channel's
    // @morfi/proto schema (different literals may carry different
    // messages) — see emitDecodeRaw. No shared decode here.
      // morfi.market.created.v0
      if (/^morfi\.market\.created\.v0$/.test(subject)) {
        if ((args as Record<string, unknown>)['sortBy'] !== 'createdAt') return;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketCreatedNotificationSchema, _d)
            : fromBinary(MarketCreatedNotificationSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketCreatedNotificationSchema>>;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['market']) ?? ((raw as Record<string, unknown> | undefined)?.['market']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p0.pending.add(_id);
        if (p0.timer === null) {
          p0.timer = setTimeout(() => {
            p0.timer = null;
            const ids = [...p0.pending];
            p0.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetMarketsFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/markets`;
              const params = new URLSearchParams();
              // Refresh the loaded window with the active filters and sort. The
              // returned array is the only ordering authority; the SDK never
              // ranks or inserts rows locally for this feed.
              if (args.marketType !== undefined) params.set('marketType', Array.isArray(args.marketType) ? args.marketType.join(',') : String(args.marketType));
              if (args.classification !== undefined) params.set('classification', Array.isArray(args.classification) ? args.classification.join(',') : String(args.classification));
              if (args.status !== undefined) params.set('status', Array.isArray(args.status) ? args.status.join(',') : String(args.status));
              if (args.statusNot !== undefined) params.set('statusNot', Array.isArray(args.statusNot) ? args.statusNot.join(',') : String(args.statusNot));
              if (args.category !== undefined) params.set('category', Array.isArray(args.category) ? args.category.join(',') : String(args.category));
              if (args.location !== undefined) params.set('location', Array.isArray(args.location) ? args.location.join(',') : String(args.location));
              if (args.search !== undefined) params.set('search', Array.isArray(args.search) ? args.search.join(',') : String(args.search));
              if (args.creator !== undefined) params.set('creator', Array.isArray(args.creator) ? args.creator.join(',') : String(args.creator));
              if (args.creatorUserId !== undefined) params.set('creatorUserId', Array.isArray(args.creatorUserId) ? args.creatorUserId.join(',') : String(args.creatorUserId));
              if (args.pubkeys !== undefined) params.set('pubkeys', Array.isArray(args.pubkeys) ? args.pubkeys.join(',') : String(args.pubkeys));
              if (args.volumeMin !== undefined) params.set('volumeMin', Array.isArray(args.volumeMin) ? args.volumeMin.join(',') : String(args.volumeMin));
              if (args.volumeMax !== undefined) params.set('volumeMax', Array.isArray(args.volumeMax) ? args.volumeMax.join(',') : String(args.volumeMax));
              if (args.createdAfter !== undefined) params.set('createdAfter', Array.isArray(args.createdAfter) ? args.createdAfter.join(',') : String(args.createdAfter));
              if (args.createdBefore !== undefined) params.set('createdBefore', Array.isArray(args.createdBefore) ? args.createdBefore.join(',') : String(args.createdBefore));
              if (args.outcomesMin !== undefined) params.set('outcomesMin', Array.isArray(args.outcomesMin) ? args.outcomesMin.join(',') : String(args.outcomesMin));
              if (args.outcomesMax !== undefined) params.set('outcomesMax', Array.isArray(args.outcomesMax) ? args.outcomesMax.join(',') : String(args.outcomesMax));
              if (args.prizePoolMin !== undefined) params.set('prizePoolMin', Array.isArray(args.prizePoolMin) ? args.prizePoolMin.join(',') : String(args.prizePoolMin));
              if (args.prizePoolMax !== undefined) params.set('prizePoolMax', Array.isArray(args.prizePoolMax) ? args.prizePoolMax.join(',') : String(args.prizePoolMax));
              if (args.liquidityMin !== undefined) params.set('liquidityMin', Array.isArray(args.liquidityMin) ? args.liquidityMin.join(',') : String(args.liquidityMin));
              if (args.liquidityMax !== undefined) params.set('liquidityMax', Array.isArray(args.liquidityMax) ? args.liquidityMax.join(',') : String(args.liquidityMax));
              if (args.backersMin !== undefined) params.set('backersMin', Array.isArray(args.backersMin) ? args.backersMin.join(',') : String(args.backersMin));
              if (args.backersMax !== undefined) params.set('backersMax', Array.isArray(args.backersMax) ? args.backersMax.join(',') : String(args.backersMax));
              if (args.tradersMin !== undefined) params.set('tradersMin', Array.isArray(args.tradersMin) ? args.tradersMin.join(',') : String(args.tradersMin));
              if (args.tradersMax !== undefined) params.set('tradersMax', Array.isArray(args.tradersMax) ? args.tradersMax.join(',') : String(args.tradersMax));
              if (args.sortBy !== undefined) params.set('sortBy', Array.isArray(args.sortBy) ? args.sortBy.join(',') : String(args.sortBy));
              if (args.sortOrder !== undefined) params.set('sortOrder', Array.isArray(args.sortOrder) ? args.sortOrder.join(',') : String(args.sortOrder));
              const cached = queryClient.getQueryData(queryKey) as PagedData<GetMarketsRow> | undefined;
              const loadedCount = cached?.data.length ?? 24;
              params.set(
                'limit',
                String(Math.max(24, Math.min(loadedCount, 100))),
              );
              const fetched: GetMarketsRow[] = [];
              let json: { data?: ReadonlyArray<GetMarketsRow> | Record<string, GetMarketsRow>; nextCursor?: string | null };
              while (true) {
              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetMarkets.refresh',
                routeTemplate: '/v1/markets',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetMarkets refresh fetch failed:', res.status);
                return;
              }
              json = (await res.json()) as { data?: ReadonlyArray<GetMarketsRow> | Record<string, GetMarketsRow>; nextCursor?: string | null };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const pageRows = Array.isArray(rawData) ? rawData : Object.values(rawData ?? {});
              fetched.push(...pageRows);
              const cursor = json.nextCursor;
              if (fetched.length >= loadedCount || typeof cursor !== 'string' || cursor.length === 0 || pageRows.length === 0) break;
              params.set('cursor', cursor);
              params.set('limit', String(Math.min(loadedCount - fetched.length, 100)));
              }
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as PagedData<GetMarketsRow>;
                const nextCursor =
                  typeof json.nextCursor === 'string' && json.nextCursor.length > 0
                    ? json.nextCursor
                    : null;
                return {
                  ...pd,
                  data: fetched,
                  nextCursor,
                  hasMore: nextCursor !== null,
                } as PagedData<GetMarketsRow>;
              });
            })();
          }, 1000);
        }
      }
      // morfi.market.${m}.resolution.v0
      if (/^morfi\.market\.[^.]+\.resolution\.v0$/.test(subject)) {
        if ((args as Record<string, unknown>)['sortBy'] !== 'resolvedAt') return;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketResolvedSchema, _d)
            : fromBinary(MarketResolvedSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketResolvedSchema>>;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['market']) ?? ((raw as Record<string, unknown> | undefined)?.['market']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p1.pending.add(_id);
        if (p1.timer === null) {
          p1.timer = setTimeout(() => {
            p1.timer = null;
            const ids = [...p1.pending];
            p1.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetMarketsFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/markets`;
              const params = new URLSearchParams();
              // Refresh the loaded window with the active filters and sort. The
              // returned array is the only ordering authority; the SDK never
              // ranks or inserts rows locally for this feed.
              if (args.marketType !== undefined) params.set('marketType', Array.isArray(args.marketType) ? args.marketType.join(',') : String(args.marketType));
              if (args.classification !== undefined) params.set('classification', Array.isArray(args.classification) ? args.classification.join(',') : String(args.classification));
              if (args.status !== undefined) params.set('status', Array.isArray(args.status) ? args.status.join(',') : String(args.status));
              if (args.statusNot !== undefined) params.set('statusNot', Array.isArray(args.statusNot) ? args.statusNot.join(',') : String(args.statusNot));
              if (args.category !== undefined) params.set('category', Array.isArray(args.category) ? args.category.join(',') : String(args.category));
              if (args.location !== undefined) params.set('location', Array.isArray(args.location) ? args.location.join(',') : String(args.location));
              if (args.search !== undefined) params.set('search', Array.isArray(args.search) ? args.search.join(',') : String(args.search));
              if (args.creator !== undefined) params.set('creator', Array.isArray(args.creator) ? args.creator.join(',') : String(args.creator));
              if (args.creatorUserId !== undefined) params.set('creatorUserId', Array.isArray(args.creatorUserId) ? args.creatorUserId.join(',') : String(args.creatorUserId));
              if (args.pubkeys !== undefined) params.set('pubkeys', Array.isArray(args.pubkeys) ? args.pubkeys.join(',') : String(args.pubkeys));
              if (args.volumeMin !== undefined) params.set('volumeMin', Array.isArray(args.volumeMin) ? args.volumeMin.join(',') : String(args.volumeMin));
              if (args.volumeMax !== undefined) params.set('volumeMax', Array.isArray(args.volumeMax) ? args.volumeMax.join(',') : String(args.volumeMax));
              if (args.createdAfter !== undefined) params.set('createdAfter', Array.isArray(args.createdAfter) ? args.createdAfter.join(',') : String(args.createdAfter));
              if (args.createdBefore !== undefined) params.set('createdBefore', Array.isArray(args.createdBefore) ? args.createdBefore.join(',') : String(args.createdBefore));
              if (args.outcomesMin !== undefined) params.set('outcomesMin', Array.isArray(args.outcomesMin) ? args.outcomesMin.join(',') : String(args.outcomesMin));
              if (args.outcomesMax !== undefined) params.set('outcomesMax', Array.isArray(args.outcomesMax) ? args.outcomesMax.join(',') : String(args.outcomesMax));
              if (args.prizePoolMin !== undefined) params.set('prizePoolMin', Array.isArray(args.prizePoolMin) ? args.prizePoolMin.join(',') : String(args.prizePoolMin));
              if (args.prizePoolMax !== undefined) params.set('prizePoolMax', Array.isArray(args.prizePoolMax) ? args.prizePoolMax.join(',') : String(args.prizePoolMax));
              if (args.liquidityMin !== undefined) params.set('liquidityMin', Array.isArray(args.liquidityMin) ? args.liquidityMin.join(',') : String(args.liquidityMin));
              if (args.liquidityMax !== undefined) params.set('liquidityMax', Array.isArray(args.liquidityMax) ? args.liquidityMax.join(',') : String(args.liquidityMax));
              if (args.backersMin !== undefined) params.set('backersMin', Array.isArray(args.backersMin) ? args.backersMin.join(',') : String(args.backersMin));
              if (args.backersMax !== undefined) params.set('backersMax', Array.isArray(args.backersMax) ? args.backersMax.join(',') : String(args.backersMax));
              if (args.tradersMin !== undefined) params.set('tradersMin', Array.isArray(args.tradersMin) ? args.tradersMin.join(',') : String(args.tradersMin));
              if (args.tradersMax !== undefined) params.set('tradersMax', Array.isArray(args.tradersMax) ? args.tradersMax.join(',') : String(args.tradersMax));
              if (args.sortBy !== undefined) params.set('sortBy', Array.isArray(args.sortBy) ? args.sortBy.join(',') : String(args.sortBy));
              if (args.sortOrder !== undefined) params.set('sortOrder', Array.isArray(args.sortOrder) ? args.sortOrder.join(',') : String(args.sortOrder));
              const cached = queryClient.getQueryData(queryKey) as PagedData<GetMarketsRow> | undefined;
              const loadedCount = cached?.data.length ?? 24;
              params.set(
                'limit',
                String(Math.max(24, Math.min(loadedCount, 100))),
              );
              const fetched: GetMarketsRow[] = [];
              let json: { data?: ReadonlyArray<GetMarketsRow> | Record<string, GetMarketsRow>; nextCursor?: string | null };
              while (true) {
              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetMarkets.refresh',
                routeTemplate: '/v1/markets',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetMarkets refresh fetch failed:', res.status);
                return;
              }
              json = (await res.json()) as { data?: ReadonlyArray<GetMarketsRow> | Record<string, GetMarketsRow>; nextCursor?: string | null };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const pageRows = Array.isArray(rawData) ? rawData : Object.values(rawData ?? {});
              fetched.push(...pageRows);
              const cursor = json.nextCursor;
              if (fetched.length >= loadedCount || typeof cursor !== 'string' || cursor.length === 0 || pageRows.length === 0) break;
              params.set('cursor', cursor);
              params.set('limit', String(Math.min(loadedCount - fetched.length, 100)));
              }
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as PagedData<GetMarketsRow>;
                const nextCursor =
                  typeof json.nextCursor === 'string' && json.nextCursor.length > 0
                    ? json.nextCursor
                    : null;
                return {
                  ...pd,
                  data: fetched,
                  nextCursor,
                  hasMore: nextCursor !== null,
                } as PagedData<GetMarketsRow>;
              });
            })();
          }, 1000);
        }
      }
  };
}
export const useGetMarketsRealtime = {
  domain: 'markets' as const,
  domainPins: ['morfi.market.*.user.*.trade.v0', 'morfi.market.*.user.*.boost.v0', 'morfi.market.*.lifecycle.v0', 'morfi.market.*.resolution.v0'] as readonly string[],
  rowKey: 'pubkey' as const,
  perEntitySubjects: (args: useGetMarketsArgs) => _useGetMarketsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetMarketsArgs) => _useGetMarketsRowSubjectTemplates(args),
  isTerminalEvent:
    (args: useGetMarketsArgs) =>
    (ev: MorfiEvent<unknown>): boolean =>
      _useGetMarketsIsTerminalEvent(ev, args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetMarketsArgs) =>
    (prev: PagedData<GetMarketsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetMarketsRow> | undefined =>
      _useGetMarketsApplyEvent(prev, ev, _args),
  onEvent: (args: useGetMarketsArgs, queryClient: { getQueryData: (k: readonly unknown[]) => unknown; setQueryData: (k: readonly unknown[], updater: (prev: unknown) => unknown) => void }) =>
    _useGetMarketsOnEvent(args, queryClient),
} as unknown as RealtimeSpec<useGetMarketsArgs, PagedData<GetMarketsRow>, unknown>;


// ─── useGetOfficialMarkets ────────────────────────────────────────────────────────────

export interface useGetOfficialMarketsArgs { readonly status?: string; readonly sortOrder?: string; readonly viewportGated?: boolean; }


async function _useGetOfficialMarketsQueryFn(
  args: useGetOfficialMarketsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetOfficialMarketsRow>> {
  const url = '/v1/markets/official';
  const params = new URLSearchParams();

  if (args.status !== undefined) params.set('status', String(args.status));
  if (args.sortOrder !== undefined) params.set('sortOrder', String(args.sortOrder));
  params.set('limit', String(limitOverride ?? 24));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetOfficialMarkets',
    routeTemplate: '/v1/markets/official',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetOfficialMarketsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetOfficialMarketsFactory = Object.assign(
  (args: useGetOfficialMarketsArgs) => ({
    queryKey: ['markets', 'list', { status: args.status ?? null, sortOrder: args.sortOrder ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetOfficialMarketsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetOfficialMarkets',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['markets', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetOfficialMarketsArgs, PagedData<GetOfficialMarketsRow>>;


export const useGetOfficialMarketsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/official',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetOfficialMarketsFactory },
  parseInput: (raw: unknown): useGetOfficialMarketsArgs => raw as useGetOfficialMarketsArgs,
  parseResponse: (raw: unknown): PagedData<GetOfficialMarketsRow> => raw as PagedData<GetOfficialMarketsRow>,
  specName: 'useGetOfficialMarkets' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['markets', 'entity', id] as readonly unknown[],
  entityProjection: {"resource":"markets"} as const,
  nestedEntityProjection: {"field":"outcomes","resource":"outcomes","idField":"mint","referenceFields":["mint","outcomeIndex"],"fieldMap":{"totalSupply":"indexedOutcomeMintSupplyD6"},"canonicalIdField":"pubkey","parentIdField":"pubkey","canonicalParentField":"market"} as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetOfficialMarketsRow>>;
export const useGetOfficialMarketsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 24,
  maxPageSize: 100,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetOfficialMarketsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetOfficialMarketsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetOfficialMarketsPerEntitySubjects(_args: useGetOfficialMarketsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = ['morfi.market.*.user.*.trade.v0', 'morfi.market.*.user.*.boost.v0', 'morfi.market.*.lifecycle.v0', 'morfi.market.*.resolution.v0'];
  return [...new Set(_subs)];
}

function _useGetOfficialMarketsRowSubjectTemplates(_args: useGetOfficialMarketsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = ['morfi.market.{pubkey}.user.*.trade.v0', 'morfi.market.{pubkey}.user.*.boost.v0', 'morfi.market.{pubkey}.lifecycle.v0', 'morfi.market.{pubkey}.resolution.v0'];
  return _tmpls;
}

function _useGetOfficialMarketsIsTerminalEvent(
  ev: MorfiEvent<unknown>,
  args: useGetOfficialMarketsArgs,
): boolean {
  const subject = ev.subject;
  if (typeof subject !== 'string') return false;
  if ((args as Record<string, unknown>)['status'] === "trading" && /^morfi\.market\.([^.]+)\.resolution\.v0$/.test(subject)) {
    return true;
  }
  return false;
}

function _useGetOfficialMarketsApplyEvent(
  prev: PagedData<GetOfficialMarketsRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetOfficialMarketsArgs,
): PagedData<GetOfficialMarketsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // morfi.market.${m}.user.*.trade.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.trade\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { pubkey?: string }).pubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetOfficialMarketsRow;
      let updated: GetOfficialMarketsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
        {
          const _arr = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['postOutcomeConvictionPpb']) ?? ((raw as Record<string, unknown> | undefined)?.['postOutcomeConvictionPpb']));
          const _list = (updated as unknown as { outcomes?: ReadonlyArray<Record<string, unknown>> }).outcomes;
          if (Array.isArray(_arr) && Array.isArray(_list)) {
            updated = {
              ...updated,
              outcomes: _list.map((__o) => {
                const __idxRaw = __o['outcomeIndex'];
                const __idx = typeof __idxRaw === 'number' ? __idxRaw : Number(__idxRaw);
                return Number.isInteger(__idx) && __idx >= 0 && __idx < _arr.length && _arr[__idx] !== undefined
                  ? { ...__o, convictionAnchor: String(_arr[__idx]) }
                  : __o;
              }),
            } as GetOfficialMarketsRow;
          }
        }
        {
          const _arr = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['postOutcomeConvictionPpb']) ?? ((raw as Record<string, unknown> | undefined)?.['postOutcomeConvictionPpb']));
          const _list = (updated as unknown as { outcomes?: ReadonlyArray<Record<string, unknown>> }).outcomes;
          if (Array.isArray(_arr) && Array.isArray(_list)) {
            updated = {
              ...updated,
              outcomes: _list.map((__o) => {
                const __idxRaw = __o['outcomeIndex'];
                const __idx = typeof __idxRaw === 'number' ? __idxRaw : Number(__idxRaw);
                return Number.isInteger(__idx) && __idx >= 0 && __idx < _arr.length && _arr[__idx] !== undefined
                  ? { ...__o, conviction: String(_arr[__idx]) }
                  : __o;
              }),
            } as GetOfficialMarketsRow;
          }
        }
        {
          const _arr = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['postOutcomeConvictionTargetPpb']) ?? ((raw as Record<string, unknown> | undefined)?.['postOutcomeConvictionTargetPpb']));
          const _list = (updated as unknown as { outcomes?: ReadonlyArray<Record<string, unknown>> }).outcomes;
          if (Array.isArray(_arr) && Array.isArray(_list)) {
            updated = {
              ...updated,
              outcomes: _list.map((__o) => {
                const __idxRaw = __o['outcomeIndex'];
                const __idx = typeof __idxRaw === 'number' ? __idxRaw : Number(__idxRaw);
                return Number.isInteger(__idx) && __idx >= 0 && __idx < _arr.length && _arr[__idx] !== undefined
                  ? { ...__o, convictionTarget: String(_arr[__idx]) }
                  : __o;
              }),
            } as GetOfficialMarketsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['ts']) ?? ((raw as Record<string, unknown> | undefined)?.['ts']));
          if (_v !== undefined) {
            updated = { ...updated, convictionUpdatedAt: String(_v) } as GetOfficialMarketsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['volumeTotal'];
            updated = { ...updated, volumeTotal: addD6String(_prevField, _v) } as GetOfficialMarketsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['prizePoolContributionD6']) ?? ((raw as Record<string, unknown> | undefined)?.['prizePoolContributionD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['prizePool'];
            updated = { ...updated, prizePool: addD6String(_prevField, _v) } as GetOfficialMarketsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['baseFeeToBackersD6']) ?? ((raw as Record<string, unknown> | undefined)?.['baseFeeToBackersD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['backerRewards'];
            updated = { ...updated, backerRewards: addD6String(_prevField, _v) } as GetOfficialMarketsRow;
          }
        }
        updated = (HELPERS.applyBcActivityToMarket as unknown as (prev: GetOfficialMarketsRow, raw: ReturnType<typeof fromBinary<typeof MarketTradeSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetOfficialMarketsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
        updated = (HELPERS.applyBcTradeMarkToMarket as unknown as (prev: GetOfficialMarketsRow, raw: ReturnType<typeof fromBinary<typeof MarketTradeSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetOfficialMarketsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // morfi.market.${m}.user.*.boost.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.boost\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { pubkey?: string }).pubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetOfficialMarketsRow;
      let updated: GetOfficialMarketsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBoostSchema, _d)
            : fromBinary(MarketBoostSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBoostSchema>>;
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['prizePool'];
            updated = { ...updated, prizePool: addD6String(_prevField, _v) } as GetOfficialMarketsRow;
          }
        }
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // morfi.market.${m}.lifecycle.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.lifecycle\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { pubkey?: string }).pubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetOfficialMarketsRow;
      let updated: GetOfficialMarketsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketLifecycleSchema, _d)
            : fromBinary(MarketLifecycleSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>;
        updated = (HELPERS.mapLifecycleStatus as unknown as (prev: GetOfficialMarketsRow, raw: ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetOfficialMarketsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // morfi.market.${m}.resolution.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.resolution\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { pubkey?: string }).pubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetOfficialMarketsRow;
      let updated: GetOfficialMarketsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketResolvedSchema, _d)
            : fromBinary(MarketResolvedSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketResolvedSchema>>;
        updated = (HELPERS.mapResolvedStatus as unknown as (prev: GetOfficialMarketsRow, raw: ReturnType<typeof fromBinary<typeof MarketResolvedSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetOfficialMarketsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
      // removeRow gate — splice this row out of the cache when the
      // generated terminal-event predicate matches. The patch above still
      // ran (the row's transient
      // updated identity briefly lives in `updated` before the splice
      // — useful for animation hooks that want to see the new field
      // value before the row disappears). When the gate doesn't match,
      // fall through to the normal updated-row replacement.
      {
        if (_useGetOfficialMarketsIsTerminalEvent(ev, args)) {
          const nextRows = prevRows.slice();
          nextRows.splice(_idx, 1);
          if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
          return { ...prev, data: nextRows, _normalizedPatchedEntity: updated } as PagedData<GetOfficialMarketsRow>;
        }
      }
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetOfficialMarketsRealtime = {
  domain: 'markets' as const,
  domainPins: ['morfi.market.*.user.*.trade.v0', 'morfi.market.*.user.*.boost.v0', 'morfi.market.*.lifecycle.v0', 'morfi.market.*.resolution.v0'] as readonly string[],
  rowKey: 'pubkey' as const,
  perEntitySubjects: (args: useGetOfficialMarketsArgs) => _useGetOfficialMarketsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetOfficialMarketsArgs) => _useGetOfficialMarketsRowSubjectTemplates(args),
  isTerminalEvent:
    (args: useGetOfficialMarketsArgs) =>
    (ev: MorfiEvent<unknown>): boolean =>
      _useGetOfficialMarketsIsTerminalEvent(ev, args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetOfficialMarketsArgs) =>
    (prev: PagedData<GetOfficialMarketsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetOfficialMarketsRow> | undefined =>
      _useGetOfficialMarketsApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetOfficialMarketsRow>, unknown>;


// ─── useGetCreatedMarketsAndProposals ────────────────────────────────────────────────────────────

export interface useGetCreatedMarketsAndProposalsArgs { readonly creatorUserId?: string; readonly group?: string; readonly status?: string; readonly sortOrder?: string; readonly pubkeys?: string; readonly viewportGated?: boolean; }


async function _useGetCreatedMarketsAndProposalsQueryFn(
  args: useGetCreatedMarketsAndProposalsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetCreatedMarketsAndProposalsRow>> {
  const url = '/v1/markets/created';
  const params = new URLSearchParams();

  if (args.creatorUserId !== undefined) params.set('creatorUserId', String(args.creatorUserId));
  if (args.group !== undefined) params.set('group', String(args.group));
  if (args.status !== undefined) params.set('status', String(args.status));
  if (args.sortOrder !== undefined) params.set('sortOrder', String(args.sortOrder));
  if (args.pubkeys !== undefined) params.set('pubkeys', String(args.pubkeys));
  params.set('limit', String(limitOverride ?? 12));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetCreatedMarketsAndProposals',
    routeTemplate: '/v1/markets/created',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetCreatedMarketsAndProposalsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    responseSubjectIds: readResponseSubjectIds(json['activeMarketPubkeys']),
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetCreatedMarketsAndProposalsFactory = Object.assign(
  (args: useGetCreatedMarketsAndProposalsArgs) => ({
    queryKey: ['markets', 'list', { creatorUserId: args.creatorUserId ?? null, group: args.group ?? null, status: args.status ?? null, sortOrder: args.sortOrder ?? null, pubkeys: args.pubkeys ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetCreatedMarketsAndProposalsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetCreatedMarketsAndProposals',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['markets', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetCreatedMarketsAndProposalsArgs, PagedData<GetCreatedMarketsAndProposalsRow>>;


export const useGetCreatedMarketsAndProposalsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/created',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetCreatedMarketsAndProposalsFactory },
  parseInput: (raw: unknown): useGetCreatedMarketsAndProposalsArgs => raw as useGetCreatedMarketsAndProposalsArgs,
  parseResponse: (raw: unknown): PagedData<GetCreatedMarketsAndProposalsRow> => raw as PagedData<GetCreatedMarketsAndProposalsRow>,
  specName: 'useGetCreatedMarketsAndProposals' as const,
  polling: { hasNats: true, intervalMs: 10000 } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['markets', 'entity', id] as readonly unknown[],
  entityProjection: {"resource":"markets"} as const,
  nestedEntityProjection: {"field":"proposal","resource":"proposals","idField":"pubkey","referenceFields":["pubkey"]} as const,
  viewerOverlay: {
    identityFields: ['myVote'] as const,
    walletFields: [] as const,
  },
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetCreatedMarketsAndProposalsRow>>;
export const useGetCreatedMarketsAndProposalsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 12,
  maxPageSize: 100,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetCreatedMarketsAndProposalsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetCreatedMarketsAndProposalsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetCreatedMarketsAndProposalsPerEntitySubjects(_args: useGetCreatedMarketsAndProposalsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = ['morfi.market.*.user.*.trade.v0', 'morfi.market.*.user.*.boost.v0', 'morfi.market.*.user.*.backing.v0', 'morfi.market.*.lifecycle.v0', 'morfi.market.*.resolution.v0', 'morfi.market.*.votes.v0'];
  return [...new Set(_subs)];
}

function _useGetCreatedMarketsAndProposalsRowSubjectTemplates(_args: useGetCreatedMarketsAndProposalsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = ['morfi.market.{pubkey}.user.*.trade.v0', 'morfi.market.{pubkey}.user.*.boost.v0', 'morfi.market.{pubkey}.user.*.backing.v0', 'morfi.market.{pubkey}.lifecycle.v0', 'morfi.market.{pubkey}.resolution.v0', 'morfi.market.{pubkey}.votes.v0'];
  return _tmpls;
}

function _useGetCreatedMarketsAndProposalsApplyEvent(
  prev: PagedData<GetCreatedMarketsAndProposalsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetCreatedMarketsAndProposalsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // morfi.market.${m}.user.*.trade.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.trade\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { pubkey?: string }).pubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetCreatedMarketsAndProposalsRow;
      let updated: GetCreatedMarketsAndProposalsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['volumeTotal'];
            updated = { ...updated, volumeTotal: addD6String(_prevField, _v) } as GetCreatedMarketsAndProposalsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['prizePoolContributionD6']) ?? ((raw as Record<string, unknown> | undefined)?.['prizePoolContributionD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['prizePool'];
            updated = { ...updated, prizePool: addD6String(_prevField, _v) } as GetCreatedMarketsAndProposalsRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['baseFeeToBackersD6']) ?? ((raw as Record<string, unknown> | undefined)?.['baseFeeToBackersD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['backerRewards'];
            updated = { ...updated, backerRewards: addD6String(_prevField, _v) } as GetCreatedMarketsAndProposalsRow;
          }
        }
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // morfi.market.${m}.user.*.boost.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.boost\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { pubkey?: string }).pubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetCreatedMarketsAndProposalsRow;
      let updated: GetCreatedMarketsAndProposalsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBoostSchema, _d)
            : fromBinary(MarketBoostSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBoostSchema>>;
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (updated as unknown as Record<string, unknown>)['prizePool'];
            updated = { ...updated, prizePool: addD6String(_prevField, _v) } as GetCreatedMarketsAndProposalsRow;
          }
        }
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

function _useGetCreatedMarketsAndProposalsOnEvent(
  args: useGetCreatedMarketsAndProposalsArgs,
  queryClient: { getQueryData: (k: readonly unknown[]) => unknown; setQueryData: (k: readonly unknown[], updater: (prev: unknown) => unknown) => void },
): (ev: MorfiEvent<unknown>) => void {
  const p0 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  const p1 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  const p2 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  const p3 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  return (ev: MorfiEvent<unknown>): void => {
    const subject = ev.subject;
    if (typeof subject !== 'string') return;
    // Each prepend branch decodes its own row var with ITS channel's
    // @morfi/proto schema (different literals may carry different
    // messages) — see emitDecodeRaw. No shared decode here.
      // morfi.market.${m}.user.*.backing.v0
      if (/^morfi\.market\.[^.]+\.user\.[^.]+\.backing\.v0$/.test(subject)) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBackingSchema, _d)
            : fromBinary(MarketBackingSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBackingSchema>>;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['market']) ?? ((raw as Record<string, unknown> | undefined)?.['market']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p0.pending.add(_id);
        if (p0.timer === null) {
          p0.timer = setTimeout(() => {
            p0.timer = null;
            const ids = [...p0.pending];
            p0.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetCreatedMarketsAndProposalsFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/markets/created`;
              const params = new URLSearchParams();
              // Refresh the loaded window with the active filters and sort. The
              // returned array is the only ordering authority; the SDK never
              // ranks or inserts rows locally for this feed.
              if (args.creatorUserId !== undefined) params.set('creatorUserId', Array.isArray(args.creatorUserId) ? args.creatorUserId.join(',') : String(args.creatorUserId));
              if (args.group !== undefined) params.set('group', Array.isArray(args.group) ? args.group.join(',') : String(args.group));
              if (args.status !== undefined) params.set('status', Array.isArray(args.status) ? args.status.join(',') : String(args.status));
              if (args.sortOrder !== undefined) params.set('sortOrder', Array.isArray(args.sortOrder) ? args.sortOrder.join(',') : String(args.sortOrder));
              if (args.pubkeys !== undefined) params.set('pubkeys', Array.isArray(args.pubkeys) ? args.pubkeys.join(',') : String(args.pubkeys));
              const cached = queryClient.getQueryData(queryKey) as PagedData<GetCreatedMarketsAndProposalsRow> | undefined;
              const loadedCount = cached?.data.length ?? 12;
              params.set(
                'limit',
                String(Math.max(12, Math.min(loadedCount, 100))),
              );
              const fetched: GetCreatedMarketsAndProposalsRow[] = [];
              let json: { data?: ReadonlyArray<GetCreatedMarketsAndProposalsRow> | Record<string, GetCreatedMarketsAndProposalsRow>; nextCursor?: string | null; activeMarketPubkeys?: unknown };
              while (true) {
              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetCreatedMarketsAndProposals.refresh',
                routeTemplate: '/v1/markets/created',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetCreatedMarketsAndProposals refresh fetch failed:', res.status);
                return;
              }
              json = (await res.json()) as { data?: ReadonlyArray<GetCreatedMarketsAndProposalsRow> | Record<string, GetCreatedMarketsAndProposalsRow>; nextCursor?: string | null; activeMarketPubkeys?: unknown };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const pageRows = Array.isArray(rawData) ? rawData : Object.values(rawData ?? {});
              fetched.push(...pageRows);
              const cursor = json.nextCursor;
              if (fetched.length >= loadedCount || typeof cursor !== 'string' || cursor.length === 0 || pageRows.length === 0) break;
              params.set('cursor', cursor);
              params.set('limit', String(Math.min(loadedCount - fetched.length, 100)));
              }
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as PagedData<GetCreatedMarketsAndProposalsRow>;
                const nextCursor =
                  typeof json.nextCursor === 'string' && json.nextCursor.length > 0
                    ? json.nextCursor
                    : null;
                return {
                  ...pd,
                  data: fetched,
                  nextCursor,
                  hasMore: nextCursor !== null,
                  responseSubjectIds: readResponseSubjectIds(json['activeMarketPubkeys']),
                } as PagedData<GetCreatedMarketsAndProposalsRow>;
              });
            })();
          }, 1000);
        }
      }
      // morfi.market.${m}.lifecycle.v0
      if (/^morfi\.market\.[^.]+\.lifecycle\.v0$/.test(subject)) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketLifecycleSchema, _d)
            : fromBinary(MarketLifecycleSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['market']) ?? ((raw as Record<string, unknown> | undefined)?.['market']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p1.pending.add(_id);
        if (p1.timer === null) {
          p1.timer = setTimeout(() => {
            p1.timer = null;
            const ids = [...p1.pending];
            p1.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetCreatedMarketsAndProposalsFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/markets/created`;
              const params = new URLSearchParams();
              // Refresh the loaded window with the active filters and sort. The
              // returned array is the only ordering authority; the SDK never
              // ranks or inserts rows locally for this feed.
              if (args.creatorUserId !== undefined) params.set('creatorUserId', Array.isArray(args.creatorUserId) ? args.creatorUserId.join(',') : String(args.creatorUserId));
              if (args.group !== undefined) params.set('group', Array.isArray(args.group) ? args.group.join(',') : String(args.group));
              if (args.status !== undefined) params.set('status', Array.isArray(args.status) ? args.status.join(',') : String(args.status));
              if (args.sortOrder !== undefined) params.set('sortOrder', Array.isArray(args.sortOrder) ? args.sortOrder.join(',') : String(args.sortOrder));
              if (args.pubkeys !== undefined) params.set('pubkeys', Array.isArray(args.pubkeys) ? args.pubkeys.join(',') : String(args.pubkeys));
              const cached = queryClient.getQueryData(queryKey) as PagedData<GetCreatedMarketsAndProposalsRow> | undefined;
              const loadedCount = cached?.data.length ?? 12;
              params.set(
                'limit',
                String(Math.max(12, Math.min(loadedCount, 100))),
              );
              const fetched: GetCreatedMarketsAndProposalsRow[] = [];
              let json: { data?: ReadonlyArray<GetCreatedMarketsAndProposalsRow> | Record<string, GetCreatedMarketsAndProposalsRow>; nextCursor?: string | null; activeMarketPubkeys?: unknown };
              while (true) {
              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetCreatedMarketsAndProposals.refresh',
                routeTemplate: '/v1/markets/created',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetCreatedMarketsAndProposals refresh fetch failed:', res.status);
                return;
              }
              json = (await res.json()) as { data?: ReadonlyArray<GetCreatedMarketsAndProposalsRow> | Record<string, GetCreatedMarketsAndProposalsRow>; nextCursor?: string | null; activeMarketPubkeys?: unknown };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const pageRows = Array.isArray(rawData) ? rawData : Object.values(rawData ?? {});
              fetched.push(...pageRows);
              const cursor = json.nextCursor;
              if (fetched.length >= loadedCount || typeof cursor !== 'string' || cursor.length === 0 || pageRows.length === 0) break;
              params.set('cursor', cursor);
              params.set('limit', String(Math.min(loadedCount - fetched.length, 100)));
              }
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as PagedData<GetCreatedMarketsAndProposalsRow>;
                const nextCursor =
                  typeof json.nextCursor === 'string' && json.nextCursor.length > 0
                    ? json.nextCursor
                    : null;
                return {
                  ...pd,
                  data: fetched,
                  nextCursor,
                  hasMore: nextCursor !== null,
                  responseSubjectIds: readResponseSubjectIds(json['activeMarketPubkeys']),
                } as PagedData<GetCreatedMarketsAndProposalsRow>;
              });
            })();
          }, 1000);
        }
      }
      // morfi.market.${m}.resolution.v0
      if (/^morfi\.market\.[^.]+\.resolution\.v0$/.test(subject)) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketResolvedSchema, _d)
            : fromBinary(MarketResolvedSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketResolvedSchema>>;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['market']) ?? ((raw as Record<string, unknown> | undefined)?.['market']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p2.pending.add(_id);
        if (p2.timer === null) {
          p2.timer = setTimeout(() => {
            p2.timer = null;
            const ids = [...p2.pending];
            p2.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetCreatedMarketsAndProposalsFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/markets/created`;
              const params = new URLSearchParams();
              // Refresh the loaded window with the active filters and sort. The
              // returned array is the only ordering authority; the SDK never
              // ranks or inserts rows locally for this feed.
              if (args.creatorUserId !== undefined) params.set('creatorUserId', Array.isArray(args.creatorUserId) ? args.creatorUserId.join(',') : String(args.creatorUserId));
              if (args.group !== undefined) params.set('group', Array.isArray(args.group) ? args.group.join(',') : String(args.group));
              if (args.status !== undefined) params.set('status', Array.isArray(args.status) ? args.status.join(',') : String(args.status));
              if (args.sortOrder !== undefined) params.set('sortOrder', Array.isArray(args.sortOrder) ? args.sortOrder.join(',') : String(args.sortOrder));
              if (args.pubkeys !== undefined) params.set('pubkeys', Array.isArray(args.pubkeys) ? args.pubkeys.join(',') : String(args.pubkeys));
              const cached = queryClient.getQueryData(queryKey) as PagedData<GetCreatedMarketsAndProposalsRow> | undefined;
              const loadedCount = cached?.data.length ?? 12;
              params.set(
                'limit',
                String(Math.max(12, Math.min(loadedCount, 100))),
              );
              const fetched: GetCreatedMarketsAndProposalsRow[] = [];
              let json: { data?: ReadonlyArray<GetCreatedMarketsAndProposalsRow> | Record<string, GetCreatedMarketsAndProposalsRow>; nextCursor?: string | null; activeMarketPubkeys?: unknown };
              while (true) {
              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetCreatedMarketsAndProposals.refresh',
                routeTemplate: '/v1/markets/created',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetCreatedMarketsAndProposals refresh fetch failed:', res.status);
                return;
              }
              json = (await res.json()) as { data?: ReadonlyArray<GetCreatedMarketsAndProposalsRow> | Record<string, GetCreatedMarketsAndProposalsRow>; nextCursor?: string | null; activeMarketPubkeys?: unknown };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const pageRows = Array.isArray(rawData) ? rawData : Object.values(rawData ?? {});
              fetched.push(...pageRows);
              const cursor = json.nextCursor;
              if (fetched.length >= loadedCount || typeof cursor !== 'string' || cursor.length === 0 || pageRows.length === 0) break;
              params.set('cursor', cursor);
              params.set('limit', String(Math.min(loadedCount - fetched.length, 100)));
              }
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as PagedData<GetCreatedMarketsAndProposalsRow>;
                const nextCursor =
                  typeof json.nextCursor === 'string' && json.nextCursor.length > 0
                    ? json.nextCursor
                    : null;
                return {
                  ...pd,
                  data: fetched,
                  nextCursor,
                  hasMore: nextCursor !== null,
                  responseSubjectIds: readResponseSubjectIds(json['activeMarketPubkeys']),
                } as PagedData<GetCreatedMarketsAndProposalsRow>;
              });
            })();
          }, 1000);
        }
      }
      // morfi.market.${m}.votes.v0
      if (/^morfi\.market\.[^.]+\.votes\.v0$/.test(subject)) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(ProposalVoteUpdateSchema, _d)
            : fromBinary(ProposalVoteUpdateSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof ProposalVoteUpdateSchema>>;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['marketPubkey']) ?? ((raw as Record<string, unknown> | undefined)?.['marketPubkey']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p3.pending.add(_id);
        if (p3.timer === null) {
          p3.timer = setTimeout(() => {
            p3.timer = null;
            const ids = [...p3.pending];
            p3.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetCreatedMarketsAndProposalsFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/markets/created`;
              const params = new URLSearchParams();
              // Refresh the loaded window with the active filters and sort. The
              // returned array is the only ordering authority; the SDK never
              // ranks or inserts rows locally for this feed.
              if (args.creatorUserId !== undefined) params.set('creatorUserId', Array.isArray(args.creatorUserId) ? args.creatorUserId.join(',') : String(args.creatorUserId));
              if (args.group !== undefined) params.set('group', Array.isArray(args.group) ? args.group.join(',') : String(args.group));
              if (args.status !== undefined) params.set('status', Array.isArray(args.status) ? args.status.join(',') : String(args.status));
              if (args.sortOrder !== undefined) params.set('sortOrder', Array.isArray(args.sortOrder) ? args.sortOrder.join(',') : String(args.sortOrder));
              if (args.pubkeys !== undefined) params.set('pubkeys', Array.isArray(args.pubkeys) ? args.pubkeys.join(',') : String(args.pubkeys));
              const cached = queryClient.getQueryData(queryKey) as PagedData<GetCreatedMarketsAndProposalsRow> | undefined;
              const loadedCount = cached?.data.length ?? 12;
              params.set(
                'limit',
                String(Math.max(12, Math.min(loadedCount, 100))),
              );
              const fetched: GetCreatedMarketsAndProposalsRow[] = [];
              let json: { data?: ReadonlyArray<GetCreatedMarketsAndProposalsRow> | Record<string, GetCreatedMarketsAndProposalsRow>; nextCursor?: string | null; activeMarketPubkeys?: unknown };
              while (true) {
              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetCreatedMarketsAndProposals.refresh',
                routeTemplate: '/v1/markets/created',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetCreatedMarketsAndProposals refresh fetch failed:', res.status);
                return;
              }
              json = (await res.json()) as { data?: ReadonlyArray<GetCreatedMarketsAndProposalsRow> | Record<string, GetCreatedMarketsAndProposalsRow>; nextCursor?: string | null; activeMarketPubkeys?: unknown };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const pageRows = Array.isArray(rawData) ? rawData : Object.values(rawData ?? {});
              fetched.push(...pageRows);
              const cursor = json.nextCursor;
              if (fetched.length >= loadedCount || typeof cursor !== 'string' || cursor.length === 0 || pageRows.length === 0) break;
              params.set('cursor', cursor);
              params.set('limit', String(Math.min(loadedCount - fetched.length, 100)));
              }
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as PagedData<GetCreatedMarketsAndProposalsRow>;
                const nextCursor =
                  typeof json.nextCursor === 'string' && json.nextCursor.length > 0
                    ? json.nextCursor
                    : null;
                return {
                  ...pd,
                  data: fetched,
                  nextCursor,
                  hasMore: nextCursor !== null,
                  responseSubjectIds: readResponseSubjectIds(json['activeMarketPubkeys']),
                } as PagedData<GetCreatedMarketsAndProposalsRow>;
              });
            })();
          }, 1000);
        }
      }
  };
}
export const useGetCreatedMarketsAndProposalsRealtime = {
  domain: 'markets' as const,
  responseSubjects: {"field":"activeMarketPubkeys","templates":["morfi.market.{id}.lifecycle.v0","morfi.market.{id}.resolution.v0","morfi.market.{id}.user.*.backing.v0"]} as const,
  domainPins: ['morfi.market.*.user.*.trade.v0', 'morfi.market.*.user.*.boost.v0'] as readonly string[],
  rowKey: 'pubkey' as const,
  perEntitySubjects: (args: useGetCreatedMarketsAndProposalsArgs) => _useGetCreatedMarketsAndProposalsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetCreatedMarketsAndProposalsArgs) => _useGetCreatedMarketsAndProposalsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetCreatedMarketsAndProposalsArgs) =>
    (prev: PagedData<GetCreatedMarketsAndProposalsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetCreatedMarketsAndProposalsRow> | undefined =>
      _useGetCreatedMarketsAndProposalsApplyEvent(prev, ev),
  onEvent: (args: useGetCreatedMarketsAndProposalsArgs, queryClient: { getQueryData: (k: readonly unknown[]) => unknown; setQueryData: (k: readonly unknown[], updater: (prev: unknown) => unknown) => void }) =>
    _useGetCreatedMarketsAndProposalsOnEvent(args, queryClient),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetCreatedMarketsAndProposalsRow>, unknown>;


// ─── useGetMarketRecap ────────────────────────────────────────────────────────────

export interface useGetMarketRecapArgs { readonly pubkey: string; readonly fields?: readonly ('pubkey' | 'title' | 'description' | 'coverImageUrl' | 'coverImageNsfw' | 'resolvedAt' | 'winningOutcomeIndex' | 'winningFinalConsensusPpb' | 'participantCount' | 'volumeAtResolution' | 'outcomes')[]; }


async function _useGetMarketRecapQueryFn(
  args: useGetMarketRecapArgs,
  ctx: QueryFunctionContext,
): Promise<GetMarketRecapRow | null> {
  if (args.pubkey === undefined || args.pubkey === null || args.pubkey === '') return null;
  const url = `/v1/markets/${args.pubkey}/recap`;
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetMarketRecap',
    routeTemplate: '/v1/markets/{pubkey}/recap',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetMarketRecapRow;
}


const _useGetMarketRecapFactory = Object.assign(
  (args: useGetMarketRecapArgs) => ({
    queryKey: ['markets', 'detail', args.pubkey, 'recap'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMarketRecapQueryFn(args, ctx),
    staleTime: 86400000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetMarketRecap',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['markets', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMarketRecapArgs, GetMarketRecapRow>;


export const useGetMarketRecapSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/{pubkey}/recap',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetMarketRecapFactory },
  parseInput: (raw: unknown): useGetMarketRecapArgs => raw as useGetMarketRecapArgs,
  parseResponse: (raw: unknown): GetMarketRecapRow => raw as GetMarketRecapRow,
  specName: 'useGetMarketRecap' as const,
  polling: { hasNats: false, intervalMs: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetMarketRecapRow>;


// ─── useGetMarket ────────────────────────────────────────────────────────────

export interface useGetMarketArgs { readonly pubkey: string; readonly mints?: readonly string[]; }


async function _useGetMarketQueryFn(
  args: useGetMarketArgs,
  ctx: QueryFunctionContext,
): Promise<GetMarketRow | null> {
  if (args.pubkey === undefined || args.pubkey === null || args.pubkey === '') return null;
  const url = `/v1/markets/${args.pubkey}`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.

  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetMarket',
    routeTemplate: '/v1/markets/{pubkey}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetMarketRow;
}


const _useGetMarketFactory = Object.assign(
  (args: useGetMarketArgs) => ({
    queryKey: ['markets', 'entity', args.pubkey] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMarketQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.data === null ||
      typeof query.state.data !== 'object' ||
      !('description' in query.state.data) ||
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetMarket',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['markets', 'entity', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMarketArgs, GetMarketRow>;


export const useGetMarketSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/{pubkey}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetMarketFactory },
  parseInput: (raw: unknown): useGetMarketArgs => raw as useGetMarketArgs,
  parseResponse: (raw: unknown): GetMarketRow => raw as GetMarketRow,
  specName: 'useGetMarket' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  // MOR-859 — the detail hook reads and writes the same canonical entity as
  // every normalized market list. The completeness guard above still forces
  // a detail fetch when this key currently holds only the narrower list DTO.
  canonicalEntity: true as const,
  entityQueryKey: (id: string) => ['markets', 'entity', id] as readonly unknown[],
  nestedEntityProjection: {"field":"outcomes","resource":"outcomes","idField":"mint","referenceFields":["mint","outcomeIndex"],"fieldMap":{"totalSupply":"indexedOutcomeMintSupplyD6"},"canonicalIdField":"pubkey","parentIdField":"pubkey","canonicalParentField":"market"} as const,
} as unknown as HookSpec<useGetMarketArgs, GetMarketRow>;

function _useGetMarketPerEntitySubjects(args: useGetMarketArgs): readonly string[] {
  const subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.trade.v0`, `morfi.market.${args.pubkey}.user.*.boost.v0`, `morfi.market.${args.pubkey}.lifecycle.v0`, `morfi.market.${args.pubkey}.resolution.v0`, ...(args.mints ?? []).map((_wp) => `morfi.amm.${_wp}.user.*.activity.v0`)];
  // An unfilled placeholder (hook called with an empty/undefined id)
  // interpolates to an empty subject token (morfi.market..user.*.trade.v0) or to
  // the JS stringification `'undefined'` / `'null'` / `'NaN'`. JetStream
  // either rejects the malformed subject (failing the WHOLE consumer
  // update) or accepts the sentinel-string token which never matches a
  // real publish. Filter all of these per-subject so a one-arg-missing
  // call site doesn't poison the consumer for every OTHER subject the
  // hook subscribes to.
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetMarketGatedSubjects(args: useGetMarketArgs): readonly string[] {
  // Subjects fanned out from an ARRAY hook arg — viewport-gated per entity.
  const subs: readonly string[] = [...(args.mints ?? []).map((_wp) => `morfi.amm.${_wp}.user.*.activity.v0`)];
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetMarketReconcileAlwaysSubjects(args: useGetMarketArgs): readonly string[] {
  const _subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.boost.v0`];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}
function _useGetMarketApplyEvent(
  prev: GetMarketRow | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetMarketArgs,
): GetMarketRow | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  let next: GetMarketRow = prev;
    // subject "morfi.market.${m}.user.*.trade.v0"
  {
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.trade\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
        {
          const _arr = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['postOutcomeConvictionPpb']) ?? ((raw as Record<string, unknown> | undefined)?.['postOutcomeConvictionPpb']));
          const _list = (next as unknown as { outcomes?: ReadonlyArray<Record<string, unknown>> }).outcomes;
          if (Array.isArray(_arr) && Array.isArray(_list)) {
            next = {
              ...next,
              outcomes: _list.map((__o) => {
                const __idxRaw = __o['outcomeIndex'];
                const __idx = typeof __idxRaw === 'number' ? __idxRaw : Number(__idxRaw);
                return Number.isInteger(__idx) && __idx >= 0 && __idx < _arr.length && _arr[__idx] !== undefined
                  ? { ...__o, convictionAnchor: String(_arr[__idx]) }
                  : __o;
              }),
            } as GetMarketRow;
          }
        }
        {
          const _arr = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['postOutcomeConvictionPpb']) ?? ((raw as Record<string, unknown> | undefined)?.['postOutcomeConvictionPpb']));
          const _list = (next as unknown as { outcomes?: ReadonlyArray<Record<string, unknown>> }).outcomes;
          if (Array.isArray(_arr) && Array.isArray(_list)) {
            next = {
              ...next,
              outcomes: _list.map((__o) => {
                const __idxRaw = __o['outcomeIndex'];
                const __idx = typeof __idxRaw === 'number' ? __idxRaw : Number(__idxRaw);
                return Number.isInteger(__idx) && __idx >= 0 && __idx < _arr.length && _arr[__idx] !== undefined
                  ? { ...__o, conviction: String(_arr[__idx]) }
                  : __o;
              }),
            } as GetMarketRow;
          }
        }
        {
          const _arr = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['postOutcomeConvictionTargetPpb']) ?? ((raw as Record<string, unknown> | undefined)?.['postOutcomeConvictionTargetPpb']));
          const _list = (next as unknown as { outcomes?: ReadonlyArray<Record<string, unknown>> }).outcomes;
          if (Array.isArray(_arr) && Array.isArray(_list)) {
            next = {
              ...next,
              outcomes: _list.map((__o) => {
                const __idxRaw = __o['outcomeIndex'];
                const __idx = typeof __idxRaw === 'number' ? __idxRaw : Number(__idxRaw);
                return Number.isInteger(__idx) && __idx >= 0 && __idx < _arr.length && _arr[__idx] !== undefined
                  ? { ...__o, convictionTarget: String(_arr[__idx]) }
                  : __o;
              }),
            } as GetMarketRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['ts']) ?? ((raw as Record<string, unknown> | undefined)?.['ts']));
          if (_v !== undefined) {
            next = { ...next, convictionUpdatedAt: String(_v) } as GetMarketRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['volumeTotal'];
            next = { ...next, volumeTotal: addD6String(_prevField, _v) } as GetMarketRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['volume24h'];
            next = { ...next, volume24h: addD6String(_prevField, _v) } as GetMarketRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['volume7d'];
            next = { ...next, volume7d: addD6String(_prevField, _v) } as GetMarketRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['prizePoolContributionD6']) ?? ((raw as Record<string, unknown> | undefined)?.['prizePoolContributionD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['prizePool'];
            next = { ...next, prizePool: addD6String(_prevField, _v) } as GetMarketRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['baseFeeToBackersD6']) ?? ((raw as Record<string, unknown> | undefined)?.['baseFeeToBackersD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['backerRewards'];
            next = { ...next, backerRewards: addD6String(_prevField, _v) } as GetMarketRow;
          }
        }
        next = (HELPERS.applyBcActivityToMarket as unknown as (prev: GetMarketRow, raw: ReturnType<typeof fromBinary<typeof MarketTradeSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
        next = (HELPERS.applyBcTradeMarkToMarket as unknown as (prev: GetMarketRow, raw: ReturnType<typeof fromBinary<typeof MarketTradeSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  // subject "morfi.market.${m}.user.*.boost.v0"
  {
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.boost\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBoostSchema, _d)
            : fromBinary(MarketBoostSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBoostSchema>>;
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['prizePool'];
            next = { ...next, prizePool: addD6String(_prevField, _v) } as GetMarketRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['boostTotal'];
            next = { ...next, boostTotal: addD6String(_prevField, _v) } as GetMarketRow;
          }
        }
    }
  }
  // subject "morfi.market.${m}.lifecycle.v0"
  {
    const _m = subject.match(/^morfi\.market\.([^.]+)\.lifecycle\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketLifecycleSchema, _d)
            : fromBinary(MarketLifecycleSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>;
        next = (HELPERS.mapLifecycleStatus as unknown as (prev: GetMarketRow, raw: ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  // subject "morfi.market.${m}.resolution.v0"
  {
    const _m = subject.match(/^morfi\.market\.([^.]+)\.resolution\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketResolvedSchema, _d)
            : fromBinary(MarketResolvedSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketResolvedSchema>>;
        next = (HELPERS.mapResolvedStatus as unknown as (prev: GetMarketRow, raw: ReturnType<typeof fromBinary<typeof MarketResolvedSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  // subject "morfi.amm.${mint}.user.*.activity.v0"
  {
    const _m = subject.match(/^morfi\.amm\.([^.]+)\.user\.[^.]+\.activity\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(AmmUserActivitySchema, _d)
            : fromBinary(AmmUserActivitySchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>;
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['volumeTotal'];
            next = { ...next, volumeTotal: addD6String(_prevField, _v) } as GetMarketRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['volume24h'];
            next = { ...next, volume24h: addD6String(_prevField, _v) } as GetMarketRow;
          }
        }
        {
          const _v = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['amountD6']) ?? ((raw as Record<string, unknown> | undefined)?.['amountD6']));
          if (_v !== undefined) {
            const _prevField = (next as unknown as Record<string, unknown>)['volume7d'];
            next = { ...next, volume7d: addD6String(_prevField, _v) } as GetMarketRow;
          }
        }
        next = (HELPERS.applyAmmActivityToMarket as unknown as (prev: GetMarketRow, raw: ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  return next;
}

export const useGetMarketRealtime = {
  domain: 'markets' as const,
  domainPins: [] as readonly string[],
  rowKey: 'pubkey' as const,
  perEntitySubjects: (args: useGetMarketArgs) => _useGetMarketPerEntitySubjects(args),
  gatedSubjects: (args: useGetMarketArgs) => _useGetMarketGatedSubjects(args),
  reconcileAlwaysSubjects: (args: useGetMarketArgs) => _useGetMarketReconcileAlwaysSubjects(args),
  viewportGated: false as const,
  singleton: true as const,
  applyEvent:
    (args: useGetMarketArgs) =>
    (prev: GetMarketRow | undefined, ev: MorfiEvent<unknown>): GetMarketRow | undefined =>
      _useGetMarketApplyEvent(prev, ev, args),
} as unknown as RealtimeSpec<useGetMarketArgs, GetMarketRow, unknown>;


// ─── useGetMarketTraders ────────────────────────────────────────────────────────────

export interface useGetMarketTradersArgs { readonly pubkey: string; readonly sortBy?: 'volume' | 'realized' | 'unrealized' | 'total'; readonly sortOrder?: 'asc' | 'desc'; readonly outcomeIndex?: number | null; readonly userId?: string; readonly walletAddress?: string; readonly mints?: readonly string[]; readonly viewportGated?: boolean; readonly fields?: readonly ('participationId' | 'walletAddress' | 'realizedPnlD6' | 'realizedPnlGrossD6' | 'unrealizedPnlD6' | 'totalPnlD6' | 'volumeQuoteD6' | 'tradeCount' | 'lastTradeAt' | 'rank' | 'profile' | 'outcomes')[]; }


async function _useGetMarketTradersQueryFn(
  args: useGetMarketTradersArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetMarketTradersRow>> {
  const url = `/v1/markets/${args.pubkey}/traders`;
  const params = new URLSearchParams();

  if (args.sortBy !== undefined) params.set('sortBy', String(args.sortBy));
  if (args.sortOrder !== undefined) params.set('sortOrder', String(args.sortOrder));
  if (args.outcomeIndex !== undefined) params.set('outcomeIndex', String(args.outcomeIndex));
  if (args.userId !== undefined) params.set('userId', String(args.userId));
  if (args.walletAddress !== undefined) params.set('walletAddress', String(args.walletAddress));
  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetMarketTraders',
    routeTemplate: '/v1/markets/{pubkey}/traders',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetMarketTradersRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
    _sort: { by: args.sortBy ?? null, order: args.sortOrder ?? null },
  };
}


const _useGetMarketTradersFactory = Object.assign(
  (args: useGetMarketTradersArgs) => ({
    queryKey: ['markets', 'list', args.pubkey, { sortBy: args.sortBy ?? null, sortOrder: args.sortOrder ?? null, outcomeIndex: args.outcomeIndex ?? null, userId: args.userId ?? null, walletAddress: args.walletAddress ?? null }, ((args.fields === undefined ? undefined : [...new Set([...args.fields, 'participationId'])].sort()) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMarketTradersQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetMarketTraders',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['markets', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMarketTradersArgs, PagedData<GetMarketTradersRow>>;


export const useGetMarketTradersSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/{pubkey}/traders',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetMarketTradersFactory },
  parseInput: (raw: unknown): useGetMarketTradersArgs => raw as useGetMarketTradersArgs,
  parseResponse: (raw: unknown): PagedData<GetMarketTradersRow> => raw as PagedData<GetMarketTradersRow>,
  specName: 'useGetMarketTraders' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['participation', 'entity', id] as readonly unknown[],
  entityProjection: {"resource":"participation","indexFields":["rank","unrealizedPnlD6","totalPnlD6"]} as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetMarketTradersRow>>;
export const useGetMarketTradersPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetMarketTradersArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetMarketTradersQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetMarketTradersSubjectArgValues(value: unknown): readonly string[] {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];
  const values = new Set<string>();
  for (const rawValue of rawValues) {
    if (typeof rawValue !== 'string') continue;
    const normalized = rawValue.trim();
    if (normalized !== '') values.add(normalized);
  }
  return [...values];
}

function _useGetMarketTradersHasSubjectArg(value: unknown): boolean {
  return _useGetMarketTradersSubjectArgValues(value).length > 0;
}

function _useGetMarketTradersPerEntitySubjects(args: useGetMarketTradersArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.trade.v0`, ...(args.mints ?? []).map((_wp) => `morfi.amm.${_wp}.user.*.activity.v0`), `morfi.market.${args.pubkey}.user.*.claim.v0`, `morfi.market.${args.pubkey}.resolution.v0`, `morfi.market.${args.pubkey}.lifecycle.v0`, `morfi.market.${args.pubkey}.user.*.boost.v0`];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetMarketTradersRowSubjectTemplates(args: useGetMarketTradersArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [`morfi.market.${args.pubkey}.user.*.trade.v0`, ...(args.mints ?? []).map((_wp) => `morfi.amm.${_wp}.user.*.activity.v0`), `morfi.market.${args.pubkey}.user.*.claim.v0`, `morfi.market.${args.pubkey}.resolution.v0`, `morfi.market.${args.pubkey}.lifecycle.v0`, `morfi.market.${args.pubkey}.user.*.boost.v0`];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetMarketTradersGatedSubjects(args: useGetMarketTradersArgs): readonly string[] {
  // Subjects fanned out from an ARRAY hook arg. The runtime holds each one
  // only while the entity it names is visible in some row on the page.
  const _subs: readonly string[] = [...(args.mints ?? []).map((_wp) => `morfi.amm.${_wp}.user.*.activity.v0`)];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetMarketTradersReconcileAlwaysSubjects(args: useGetMarketTradersArgs): readonly string[] {
  const _subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.trade.v0`, ...(args.mints ?? []).map((_wp) => `morfi.amm.${_wp}.user.*.activity.v0`), `morfi.market.${args.pubkey}.user.*.claim.v0`, `morfi.market.${args.pubkey}.resolution.v0`, `morfi.market.${args.pubkey}.lifecycle.v0`, `morfi.market.${args.pubkey}.user.*.boost.v0`];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}
function _useGetMarketTradersApplyEvent(
  prev: PagedData<GetMarketTradersRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetMarketTradersArgs,
): PagedData<GetMarketTradersRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // applyEvent · morfi.market.${m}.user.*.trade.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.trade\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
      const _next = HELPERS.retainParticipantRelation(
        prev,
        raw,
        args as unknown as Record<string, unknown>,
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // applyEvent · morfi.amm.${mint}.user.*.activity.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.amm\.([^.]+)\.user\.[^.]+\.activity\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(AmmUserActivitySchema, _d)
            : fromBinary(AmmUserActivitySchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>;
      const _next = HELPERS.retainParticipantRelation(
        prev,
        raw,
        args as unknown as Record<string, unknown>,
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // applyEvent · morfi.market.${m}.user.*.claim.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.claim\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketPrizeClaimSchema, _d)
            : fromBinary(MarketPrizeClaimSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketPrizeClaimSchema>>;
      const _next = HELPERS.retainParticipantRelation(
        prev,
        raw,
        args as unknown as Record<string, unknown>,
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // applyEvent · morfi.market.${m}.resolution.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.resolution\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketResolvedSchema, _d)
            : fromBinary(MarketResolvedSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketResolvedSchema>>;
      const _next = HELPERS.retainParticipantRelation(
        prev,
        raw,
        args as unknown as Record<string, unknown>,
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // applyEvent · morfi.market.${m}.lifecycle.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.lifecycle\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketLifecycleSchema, _d)
            : fromBinary(MarketLifecycleSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>;
      const _next = HELPERS.retainParticipantRelation(
        prev,
        raw,
        args as unknown as Record<string, unknown>,
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // applyEvent · morfi.market.${m}.user.*.boost.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.boost\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBoostSchema, _d)
            : fromBinary(MarketBoostSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBoostSchema>>;
      const _next = HELPERS.retainParticipantRelation(
        prev,
        raw,
        args as unknown as Record<string, unknown>,
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetMarketTradersRealtime = {
  domain: 'markets' as const,
  domainPins: ['morfi.market.*.user.*.trade.v0', 'morfi.amm.*.user.*.activity.v0', 'morfi.market.*.user.*.claim.v0', 'morfi.market.*.resolution.v0', 'morfi.market.*.lifecycle.v0', 'morfi.market.*.user.*.boost.v0'] as readonly string[],
  rowKey: 'participationId' as const,
  perEntitySubjects: (args: useGetMarketTradersArgs) => _useGetMarketTradersPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetMarketTradersArgs) => _useGetMarketTradersRowSubjectTemplates(args),
  gatedSubjects: (args: useGetMarketTradersArgs) => _useGetMarketTradersGatedSubjects(args),
  reconcileAlwaysSubjects: (args: useGetMarketTradersArgs) => _useGetMarketTradersReconcileAlwaysSubjects(args),
  viewportEntityArg: 'pubkey' as const,
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetMarketTradersArgs) =>
    (prev: PagedData<GetMarketTradersRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetMarketTradersRow> | undefined =>
      _useGetMarketTradersApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetMarketTradersRow>, unknown>;


// ─── useGetMarketBackers ────────────────────────────────────────────────────────────

export interface useGetMarketBackersArgs { readonly pubkey: string; readonly sortBy?: string; readonly sortOrder?: string; readonly viewportGated?: boolean; readonly fields?: readonly ('participationId' | 'totalBackedD6' | 'shareBalanceD6' | 'totalClaimedD6' | 'earningsD6' | 'firstBackedAt' | 'lastBackedAt' | 'rank' | 'backer')[]; }


async function _useGetMarketBackersQueryFn(
  args: useGetMarketBackersArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetMarketBackersRow>> {
  const url = `/v1/markets/${args.pubkey}/backers`;
  const params = new URLSearchParams();

  if (args.sortBy !== undefined) params.set('sortBy', String(args.sortBy));
  if (args.sortOrder !== undefined) params.set('sortOrder', String(args.sortOrder));
  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetMarketBackers',
    routeTemplate: '/v1/markets/{pubkey}/backers',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetMarketBackersRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
    _sort: { by: args.sortBy ?? null, order: args.sortOrder ?? null },
  };
}


const _useGetMarketBackersFactory = Object.assign(
  (args: useGetMarketBackersArgs) => ({
    queryKey: ['markets', 'list', args.pubkey, { sortBy: args.sortBy ?? null, sortOrder: args.sortOrder ?? null }, ((args.fields === undefined ? undefined : [...new Set([...args.fields, 'participationId'])].sort()) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMarketBackersQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetMarketBackers',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['markets', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMarketBackersArgs, PagedData<GetMarketBackersRow>>;


export const useGetMarketBackersSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/{pubkey}/backers',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetMarketBackersFactory },
  parseInput: (raw: unknown): useGetMarketBackersArgs => raw as useGetMarketBackersArgs,
  parseResponse: (raw: unknown): PagedData<GetMarketBackersRow> => raw as PagedData<GetMarketBackersRow>,
  specName: 'useGetMarketBackers' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['participation', 'entity', id] as readonly unknown[],
  entityProjection: {"resource":"participation","indexFields":["rank","earningsD6"]} as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetMarketBackersRow>>;
export const useGetMarketBackersPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetMarketBackersArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetMarketBackersQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetMarketBackersSubjectArgValues(value: unknown): readonly string[] {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];
  const values = new Set<string>();
  for (const rawValue of rawValues) {
    if (typeof rawValue !== 'string') continue;
    const normalized = rawValue.trim();
    if (normalized !== '') values.add(normalized);
  }
  return [...values];
}

function _useGetMarketBackersHasSubjectArg(value: unknown): boolean {
  return _useGetMarketBackersSubjectArgValues(value).length > 0;
}

function _useGetMarketBackersPerEntitySubjects(args: useGetMarketBackersArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.backing.v0`, `morfi.market.${args.pubkey}.backer_fee_claim.v0`, ...(args.sortBy === "earnings" ? [`morfi.market.${args.pubkey}.user.*.trade.v0`] : [])];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetMarketBackersRowSubjectTemplates(args: useGetMarketBackersArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [`morfi.market.${args.pubkey}.user.*.backing.v0`, `morfi.market.${args.pubkey}.backer_fee_claim.v0`, ...(args.sortBy === "earnings" ? [`morfi.market.${args.pubkey}.user.*.trade.v0`] : [])];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetMarketBackersReconcileAlwaysSubjects(args: useGetMarketBackersArgs): readonly string[] {
  const _subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.backing.v0`, `morfi.market.${args.pubkey}.backer_fee_claim.v0`, ...(args.sortBy === "earnings" ? [`morfi.market.${args.pubkey}.user.*.trade.v0`] : [])];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}
function _useGetMarketBackersApplyEvent(
  prev: PagedData<GetMarketBackersRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetMarketBackersArgs,
): PagedData<GetMarketBackersRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // applyEvent · morfi.market.${m}.user.*.backing.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.backing\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBackingSchema, _d)
            : fromBinary(MarketBackingSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBackingSchema>>;
      const _next = HELPERS.retainParticipantRelation(
        prev,
        raw,
        args as unknown as Record<string, unknown>,
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // applyEvent · morfi.market.${m}.backer_fee_claim.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.backer_fee_claim\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(BackerFeeClaimEventSchema, _d)
            : fromBinary(BackerFeeClaimEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof BackerFeeClaimEventSchema>>;
      const _next = HELPERS.retainParticipantRelation(
        prev,
        raw,
        args as unknown as Record<string, unknown>,
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // applyEvent · morfi.market.${m}.user.*.trade.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.trade\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
      const _next = HELPERS.retainParticipantRelation(
        prev,
        raw,
        args as unknown as Record<string, unknown>,
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetMarketBackersRealtime = {
  domain: 'markets' as const,
  domainPins: ['morfi.market.*.user.*.backing.v0', 'morfi.market.*.backer_fee_claim.v0', 'morfi.market.*.user.*.trade.v0'] as readonly string[],
  rowKey: 'participationId' as const,
  perEntitySubjects: (args: useGetMarketBackersArgs) => _useGetMarketBackersPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetMarketBackersArgs) => _useGetMarketBackersRowSubjectTemplates(args),
  reconcileAlwaysSubjects: (args: useGetMarketBackersArgs) => _useGetMarketBackersReconcileAlwaysSubjects(args),
  viewportEntityArg: 'pubkey' as const,
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetMarketBackersArgs) =>
    (prev: PagedData<GetMarketBackersRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetMarketBackersRow> | undefined =>
      _useGetMarketBackersApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetMarketBackersRow>, unknown>;


// ─── useGetMarketChart ────────────────────────────────────────────────────────────

export interface useGetMarketChartArgs { readonly pubkey: string; readonly outcomeIndex?: string; readonly interval?: '1s' | '5s' | '15s' | '30s' | '1m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '1d' | '1w' | 'all'; readonly property?: string; readonly chartType?: 'ohlcv' | 'line'; readonly from?: string; readonly to?: string; readonly targetBars?: number; readonly mints?: readonly string[]; readonly liveWindowSeconds?: string; readonly viewportGated?: boolean; }


async function _useGetMarketChartQueryFn(
  args: useGetMarketChartArgs,
  ctx: QueryFunctionContext,
): Promise<PagedData<GetMarketChartRow>> {
  const url = `/v1/markets/${args.pubkey}/chart`;
  const params = new URLSearchParams();

  if (args.outcomeIndex !== undefined) params.set('outcomeIndex', String(args.outcomeIndex));
  if (args.interval !== undefined) params.set('interval', String(args.interval));
  if (args.property !== undefined) params.set('property', String(args.property));
  if (args.chartType !== undefined) params.set('chartType', String(args.chartType));
  if (args.from !== undefined) params.set('from', String(args.from));
  if (args.to !== undefined) params.set('to', String(args.to));
  if (args.targetBars !== undefined) params.set('targetBars', String(args.targetBars));
  const liveRequest = HELPERS.resolveLiveChartRequestArgs(args);
  if (liveRequest.from !== undefined) params.set('from', liveRequest.from);
  if (liveRequest.to !== undefined) params.set('to', liveRequest.to);
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetch(full, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  return {
    data: (json['data'] ?? []) as readonly GetMarketChartRow[],
    nextCursor: null,
    hasMore: false,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    ...(typeof json['effectiveInterval'] === 'string' ? { effectiveInterval: json['effectiveInterval'] } : {}),
    ...(typeof json['rangeStart'] === 'string' ? { rangeStart: json['rangeStart'] } : {}),
    ...(typeof json['rangeEnd'] === 'string' ? { rangeEnd: json['rangeEnd'] } : {}),
    ...(typeof json['closedBefore'] === 'string' ? { closedBefore: json['closedBefore'] } : {}),
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
    
  };
}


const _useGetMarketChartFactory = Object.assign(
  (args: useGetMarketChartArgs) => ({
    queryKey: ['markets', 'list', args.pubkey, { outcomeIndex: args.outcomeIndex ?? null, interval: args.interval ?? null, property: args.property ?? null, chartType: args.chartType ?? null, from: args.from ?? null, to: args.to ?? null, targetBars: args.targetBars ?? null, ...(args.liveWindowSeconds === undefined ? {} : { from: null, to: null, liveWindowSeconds: args.liveWindowSeconds }) }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMarketChartQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetMarketChart',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['markets', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMarketChartArgs, PagedData<GetMarketChartRow>>;


export const useGetMarketChartSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/{pubkey}/chart',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetMarketChartFactory },
  parseInput: (raw: unknown): useGetMarketChartArgs => raw as useGetMarketChartArgs,
  parseResponse: (raw: unknown): PagedData<GetMarketChartRow> => raw as PagedData<GetMarketChartRow>,
  specName: 'useGetMarketChart' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<useGetMarketChartArgs, PagedData<GetMarketChartRow>>;


function _useGetMarketChartPerEntitySubjects(args: useGetMarketChartArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.trade.v0`, ...(args.mints ?? []).map((_wp) => `morfi.amm.${_wp}.user.*.activity.v0`)];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetMarketChartRowSubjectTemplates(args: useGetMarketChartArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [`morfi.market.${args.pubkey}.user.*.trade.v0`, ...(args.mints ?? []).map((_wp) => `morfi.amm.${_wp}.user.*.activity.v0`)];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetMarketChartApplyEvent(
  prev: PagedData<GetMarketChartRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetMarketChartArgs,
): PagedData<GetMarketChartRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // applyEvent · morfi.market.${m}.user.*.trade.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.trade\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
      const _next = HELPERS.updateLiveBucket(
        prev,
        raw,
        HELPERS.normalizeChartQueryArgs(args),
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // applyEvent · morfi.amm.${mint}.user.*.activity.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.amm\.([^.]+)\.user\.[^.]+\.activity\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(AmmUserActivitySchema, _d)
            : fromBinary(AmmUserActivitySchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>;
      const _next = HELPERS.updateLiveBucketFromAmm(
        prev,
        raw,
        HELPERS.normalizeChartQueryArgs(args),
      ) as typeof prev;
      if (_next === prev) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _next;
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetMarketChartRealtime = {
  domain: 'markets' as const,
  domainPins: ['morfi.market.*.user.*.trade.v0', 'morfi.amm.*.user.*.activity.v0'] as readonly string[],
  rowKey: 'bucket' as const,
  perEntitySubjects: (args: useGetMarketChartArgs) => _useGetMarketChartPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetMarketChartArgs) => _useGetMarketChartRowSubjectTemplates(args),
  viewportEntityArg: 'pubkey' as const,
  sequenceGateSubject:
    (args: useGetMarketChartArgs) =>
    (subjectPattern: string, event: unknown): string =>
      HELPERS.marketChartSequenceGateSubject(
        HELPERS.normalizeChartQueryArgs(args),
        subjectPattern,
        event,
      ),
  viewportGated: false as const,
  applyImmediately: true as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetMarketChartArgs) =>
    (prev: PagedData<GetMarketChartRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetMarketChartRow> | undefined =>
      _useGetMarketChartApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<useGetMarketChartArgs, PagedData<GetMarketChartRow>, unknown>;


// ─── useGetMarketConvictionChart ────────────────────────────────────────────────────────────

export interface useGetMarketConvictionChartArgs { readonly pubkey: string; readonly interval: '1s' | '5s' | '15s' | '30s' | '1m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '12h' | '1d'; readonly from: string; readonly to?: string; readonly limit: number; }


async function _useGetMarketConvictionChartQueryFn(
  args: useGetMarketConvictionChartArgs,
  ctx: QueryFunctionContext,
): Promise<GetMarketConvictionChartRow | null> {
  if (args.pubkey === undefined || args.pubkey === null || args.pubkey === '') return null;
  const url = `/v1/markets/${args.pubkey}/conviction-chart`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  if (args.interval !== undefined) params.set('interval', String(args.interval));
  if (args.from !== undefined) params.set('from', String(args.from));
  if (args.to !== undefined) params.set('to', String(args.to));
  if (args.limit !== undefined) params.set('limit', String(args.limit));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetMarketConvictionChart',
    routeTemplate: '/v1/markets/{pubkey}/conviction-chart',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetMarketConvictionChartRow;
}


const _useGetMarketConvictionChartFactory = Object.assign(
  (args: useGetMarketConvictionChartArgs) => ({
    queryKey: ['markets', 'detail', args.pubkey, 'conviction-chart', { interval: args.interval ?? null, from: args.from ?? null, to: args.to ?? null, limit: args.limit ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMarketConvictionChartQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetMarketConvictionChart',
      viewerSensitive: false,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['markets', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMarketConvictionChartArgs, GetMarketConvictionChartRow>;


export const useGetMarketConvictionChartSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/{pubkey}/conviction-chart',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetMarketConvictionChartFactory },
  parseInput: (raw: unknown): useGetMarketConvictionChartArgs => raw as useGetMarketConvictionChartArgs,
  parseResponse: (raw: unknown): GetMarketConvictionChartRow => raw as GetMarketConvictionChartRow,
  specName: 'useGetMarketConvictionChart' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: true as const,
} as unknown as HookSpec<useGetMarketConvictionChartArgs, GetMarketConvictionChartRow>;

function _useGetMarketConvictionChartPerEntitySubjects(args: useGetMarketConvictionChartArgs): readonly string[] {
  const subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.trade.v0`, `morfi.market.${args.pubkey}.resolution.v0`];
  // An unfilled placeholder (hook called with an empty/undefined id)
  // interpolates to an empty subject token (morfi.market..user.*.trade.v0) or to
  // the JS stringification `'undefined'` / `'null'` / `'NaN'`. JetStream
  // either rejects the malformed subject (failing the WHOLE consumer
  // update) or accepts the sentinel-string token which never matches a
  // real publish. Filter all of these per-subject so a one-arg-missing
  // call site doesn't poison the consumer for every OTHER subject the
  // hook subscribes to.
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetMarketConvictionChartApplyEvent(
  prev: GetMarketConvictionChartRow | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetMarketConvictionChartArgs,
): GetMarketConvictionChartRow | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  let next: GetMarketConvictionChartRow = prev;
    // subject "morfi.market.${m}.user.*.trade.v0"
  {
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.trade\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
        next = (HELPERS.applyConvictionChartTrade as unknown as (prev: GetMarketConvictionChartRow, raw: ReturnType<typeof fromBinary<typeof MarketTradeSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketConvictionChartRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  // subject "morfi.market.${m}.resolution.v0"
  {
    const _m = subject.match(/^morfi\.market\.([^.]+)\.resolution\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketResolvedSchema, _d)
            : fromBinary(MarketResolvedSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketResolvedSchema>>;
        next = (HELPERS.applyConvictionChartResolution as unknown as (prev: GetMarketConvictionChartRow, raw: ReturnType<typeof fromBinary<typeof MarketResolvedSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketConvictionChartRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  return next;
}

export const useGetMarketConvictionChartRealtime = {
  domain: 'markets' as const,
  domainPins: [] as readonly string[],
  rowKey: 'pubkey' as const,
  perEntitySubjects: (args: useGetMarketConvictionChartArgs) => _useGetMarketConvictionChartPerEntitySubjects(args),
  snapshotSequence: (data: GetMarketConvictionChartRow | undefined, subject: string) =>
    HELPERS.convictionChartSnapshotSequence(data, subject),
  viewportGated: false as const,
  applyImmediately: true as const,
  singleton: true as const,
  applyEvent:
    (args: useGetMarketConvictionChartArgs) =>
    (prev: GetMarketConvictionChartRow | undefined, ev: MorfiEvent<unknown>): GetMarketConvictionChartRow | undefined =>
      _useGetMarketConvictionChartApplyEvent(prev, ev, args),
} as unknown as RealtimeSpec<useGetMarketConvictionChartArgs, GetMarketConvictionChartRow, unknown>;


// ─── useSimilarMarketsByMarket ────────────────────────────────────────────────────────────

export interface useSimilarMarketsByMarketArgs { readonly pubkey: string; readonly limit?: number; readonly threshold?: number | null; readonly category?: string; }


async function _useSimilarMarketsByMarketQueryFn(
  args: useSimilarMarketsByMarketArgs,
  ctx: QueryFunctionContext,
): Promise<SimilarMarketsByMarketRow | null> {
  if (args.pubkey === undefined || args.pubkey === null || args.pubkey === '') return null;
  const url = `/v1/markets/${args.pubkey}/similar`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  if (args.limit !== undefined) params.set('limit', String(args.limit));
  if (args.threshold !== undefined) params.set('threshold', String(args.threshold));
  if (args.category !== undefined) params.set('category', String(args.category));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useSimilarMarketsByMarket',
    routeTemplate: '/v1/markets/{pubkey}/similar',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as SimilarMarketsByMarketRow;
}


const _useSimilarMarketsByMarketFactory = Object.assign(
  (args: useSimilarMarketsByMarketArgs) => ({
    queryKey: ['markets', 'detail', args.pubkey, 'similar', { limit: args.limit ?? null, threshold: args.threshold ?? null, category: args.category ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useSimilarMarketsByMarketQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useSimilarMarketsByMarket',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['markets', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useSimilarMarketsByMarketArgs, SimilarMarketsByMarketRow>;


export const useSimilarMarketsByMarketSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/{pubkey}/similar',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useSimilarMarketsByMarketFactory },
  parseInput: (raw: unknown): useSimilarMarketsByMarketArgs => raw as useSimilarMarketsByMarketArgs,
  parseResponse: (raw: unknown): SimilarMarketsByMarketRow => raw as SimilarMarketsByMarketRow,
  specName: 'useSimilarMarketsByMarket' as const,
  polling: { hasNats: false, intervalMs: 30000 } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<useSimilarMarketsByMarketArgs, SimilarMarketsByMarketRow>;


// ─── useSimilarWinnersByMarket ────────────────────────────────────────────────────────────

export interface useSimilarWinnersByMarketArgs { readonly pubkey: string; readonly limit?: number; readonly threshold?: number | null; readonly category?: string; }


async function _useSimilarWinnersByMarketQueryFn(
  args: useSimilarWinnersByMarketArgs,
  ctx: QueryFunctionContext,
): Promise<SimilarWinnersByMarketRow | null> {
  if (args.pubkey === undefined || args.pubkey === null || args.pubkey === '') return null;
  const url = `/v1/markets/${args.pubkey}/similar-winners`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  if (args.limit !== undefined) params.set('limit', String(args.limit));
  if (args.threshold !== undefined) params.set('threshold', String(args.threshold));
  if (args.category !== undefined) params.set('category', String(args.category));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useSimilarWinnersByMarket',
    routeTemplate: '/v1/markets/{pubkey}/similar-winners',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as SimilarWinnersByMarketRow;
}


const _useSimilarWinnersByMarketFactory = Object.assign(
  (args: useSimilarWinnersByMarketArgs) => ({
    queryKey: ['markets', 'detail', args.pubkey, 'similar-winners', { limit: args.limit ?? null, threshold: args.threshold ?? null, category: args.category ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useSimilarWinnersByMarketQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useSimilarWinnersByMarket',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['markets', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useSimilarWinnersByMarketArgs, SimilarWinnersByMarketRow>;


export const useSimilarWinnersByMarketSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/{pubkey}/similar-winners',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useSimilarWinnersByMarketFactory },
  parseInput: (raw: unknown): useSimilarWinnersByMarketArgs => raw as useSimilarWinnersByMarketArgs,
  parseResponse: (raw: unknown): SimilarWinnersByMarketRow => raw as SimilarWinnersByMarketRow,
  specName: 'useSimilarWinnersByMarket' as const,
  polling: { hasNats: false, intervalMs: 30000 } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<useSimilarWinnersByMarketArgs, SimilarWinnersByMarketRow>;


// ─── useGetMarketBoosts ────────────────────────────────────────────────────────────

export interface useGetMarketBoostsArgs { readonly pubkey: string; readonly view?: string; readonly sortBy?: string; readonly sortOrder?: string; readonly viewportGated?: boolean; readonly fields?: readonly ('boosterPubkey' | 'displayName' | 'avatarUri' | 'avatarNsfw' | 'totalAmount' | 'amount' | 'timestamp' | 'rank' | 'numBoosts' | 'lastBoostAt' | 'firstBoostAt' | 'booster')[]; }


async function _useGetMarketBoostsQueryFn(
  args: useGetMarketBoostsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetMarketBoostsRow>> {
  const url = `/v1/markets/${args.pubkey}/boosts`;
  const params = new URLSearchParams();

  if (args.view !== undefined) params.set('view', String(args.view));
  if (args.sortBy !== undefined) params.set('sortBy', String(args.sortBy));
  if (args.sortOrder !== undefined) params.set('sortOrder', String(args.sortOrder));
  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetMarketBoosts',
    routeTemplate: '/v1/markets/{pubkey}/boosts',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetMarketBoostsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetMarketBoostsFactory = Object.assign(
  (args: useGetMarketBoostsArgs) => ({
    queryKey: ['markets', 'list', args.pubkey, { view: args.view ?? null, sortBy: args.sortBy ?? null, sortOrder: args.sortOrder ?? null }, ((args.fields) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMarketBoostsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetMarketBoosts',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['markets', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMarketBoostsArgs, PagedData<GetMarketBoostsRow>>;


export const useGetMarketBoostsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/{pubkey}/boosts',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetMarketBoostsFactory },
  parseInput: (raw: unknown): useGetMarketBoostsArgs => raw as useGetMarketBoostsArgs,
  parseResponse: (raw: unknown): PagedData<GetMarketBoostsRow> => raw as PagedData<GetMarketBoostsRow>,
  specName: 'useGetMarketBoosts' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetMarketBoostsRow>>;
export const useGetMarketBoostsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetMarketBoostsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetMarketBoostsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetMarketBoostsPerEntitySubjects(args: useGetMarketBoostsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.boost.v0`];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetMarketBoostsRowSubjectTemplates(args: useGetMarketBoostsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [`morfi.market.${args.pubkey}.user.*.boost.v0`];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetMarketBoostsApplyEvent(
  prev: PagedData<GetMarketBoostsRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetMarketBoostsArgs,
): PagedData<GetMarketBoostsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // morfi.market.${m}.user.*.boost.v0  (list-wide — subject is the route scope)
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.boost\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBoostSchema, _d)
            : fromBinary(MarketBoostSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBoostSchema>>;
      let _changed = false;
      const nextRows = prevRows.slice();
      for (let _i = 0; _i < nextRows.length; _i += 1) {
        const _rowOrig = nextRows[_i] as GetMarketBoostsRow;
        let updated: GetMarketBoostsRow = _rowOrig;
        updated = (HELPERS.applyBoostToLeaderboardRow as unknown as (prev: GetMarketBoostsRow, raw: ReturnType<typeof fromBinary<typeof MarketBoostSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketBoostsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
        if (updated !== _rowOrig) {
          nextRows[_i] = updated;
          _changed = true;
        }
      }
      if (!_changed) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetMarketBoostsRealtime = {
  domain: 'markets' as const,
  domainPins: ['morfi.market.*.user.*.boost.v0'] as readonly string[],
  rowKey: 'boosterPubkey' as const,
  perEntitySubjects: (args: useGetMarketBoostsArgs) => _useGetMarketBoostsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetMarketBoostsArgs) => _useGetMarketBoostsRowSubjectTemplates(args),
  viewportEntityArg: 'pubkey' as const,
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetMarketBoostsArgs) =>
    (prev: PagedData<GetMarketBoostsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetMarketBoostsRow> | undefined =>
      _useGetMarketBoostsApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetMarketBoostsRow>, unknown>;


// ─── useGetMarketBoostEvents ────────────────────────────────────────────────────────────

export interface useGetMarketBoostEventsArgs { readonly pubkey: string; readonly view?: string; readonly viewportGated?: boolean; readonly fields?: readonly ('eventId' | 'boosterPubkey' | 'amount' | 'timestamp')[]; }


async function _useGetMarketBoostEventsQueryFn(
  args: useGetMarketBoostEventsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetMarketBoostEventsRow>> {
  const url = `/v1/markets/${args.pubkey}/boosts`;
  const params = new URLSearchParams();

  if (args.view !== undefined) params.set('view', String(args.view));
  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetMarketBoostEvents',
    routeTemplate: '/v1/markets/{pubkey}/boosts',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetMarketBoostEventsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetMarketBoostEventsFactory = Object.assign(
  (args: useGetMarketBoostEventsArgs) => ({
    queryKey: ['markets', 'list', args.pubkey, { view: args.view ?? null }, ((args.fields === undefined ? undefined : [...new Set([...args.fields, 'eventId'])].sort()) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMarketBoostEventsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetMarketBoostEvents',
      viewerSensitive: false,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['markets', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMarketBoostEventsArgs, PagedData<GetMarketBoostEventsRow>>;


export const useGetMarketBoostEventsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/{pubkey}/boosts',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetMarketBoostEventsFactory },
  parseInput: (raw: unknown): useGetMarketBoostEventsArgs => raw as useGetMarketBoostEventsArgs,
  parseResponse: (raw: unknown): PagedData<GetMarketBoostEventsRow> => raw as PagedData<GetMarketBoostEventsRow>,
  specName: 'useGetMarketBoostEvents' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetMarketBoostEventsRow>>;
export const useGetMarketBoostEventsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetMarketBoostEventsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetMarketBoostEventsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetMarketBoostEventsPerEntitySubjects(args: useGetMarketBoostEventsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.boost.v0`];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetMarketBoostEventsRowSubjectTemplates(args: useGetMarketBoostEventsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [`morfi.market.${args.pubkey}.user.*.boost.v0`];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetMarketBoostEventsApplyEvent(
  prev: PagedData<GetMarketBoostEventsRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetMarketBoostEventsArgs,
): PagedData<GetMarketBoostEventsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // prepend · morfi.market.${m}.user.*.boost.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.boost\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBoostSchema, _d)
            : fromBinary(MarketBoostSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBoostSchema>>;
      const _newRow = HELPERS.buildMarketBoostEventRow(raw, subject, args as unknown as Record<string, unknown>) as unknown as GetMarketBoostEventsRow;
      const _rowId = (_newRow as unknown as Record<string, unknown>)['eventId'];
      // Helpers signal "this event isn't for this cache slice" by
      // returning a row whose primary key is the empty string — codegen
      // bails here so we don't prepend/replace into the wrong list.
      if (typeof _rowId !== 'string' || _rowId.length === 0) return prev;
      // Prepend-if-new, replace-if-present. A later event on the SAME
      // row id (e.g. a GiftClaimed for a previously-CREATED gift, or a
      // thesis EDIT after POSTED) rebuilds the row via the helper and
      // patches it IN PLACE so the cached row reflects post-state
      // without a refetch. Order is preserved (no re-prepend, no
      // duplicate).
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        if ((prevRows[_i] as unknown as Record<string, unknown>)['eventId'] === _rowId) {
          const _patched = prevRows.slice();
          _patched[_i] = _newRow;
          if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
          return { ...prev, data: _patched };
        }
      }
      // Pagination may have deliberately loaded more than the default live
      // cap. Preserve that depth and evict only one oldest row per prepend;
      // otherwise the first realtime event truncates a complete chart/feed.
      const _retainedRows = Math.max(200, prevRows.length);
      const _capped =
        prevRows.length + 1 > _retainedRows
          ? [_newRow, ...prevRows.slice(0, _retainedRows - 1)]
          : [_newRow, ...prevRows];
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: _capped };
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetMarketBoostEventsRealtime = {
  domain: 'markets' as const,
  domainPins: ['morfi.market.*.user.*.boost.v0'] as readonly string[],
  rowKey: 'eventId' as const,
  perEntitySubjects: (args: useGetMarketBoostEventsArgs) => _useGetMarketBoostEventsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetMarketBoostEventsArgs) => _useGetMarketBoostEventsRowSubjectTemplates(args),
  viewportEntityArg: 'pubkey' as const,
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetMarketBoostEventsArgs) =>
    (prev: PagedData<GetMarketBoostEventsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetMarketBoostEventsRow> | undefined =>
      _useGetMarketBoostEventsApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetMarketBoostEventsRow>, unknown>;


// ─── useGetMarketBoosterLeaderboard ────────────────────────────────────────────────────────────

export interface useGetMarketBoosterLeaderboardArgs { readonly pubkey: string; readonly sortBy?: string; readonly sortOrder?: string; readonly viewportGated?: boolean; }


async function _useGetMarketBoosterLeaderboardQueryFn(
  args: useGetMarketBoosterLeaderboardArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetMarketBoosterLeaderboardRow>> {
  const url = `/v1/markets/${args.pubkey}/boosters/leaderboard`;
  const params = new URLSearchParams();

  if (args.sortBy !== undefined) params.set('sortBy', String(args.sortBy));
  if (args.sortOrder !== undefined) params.set('sortOrder', String(args.sortOrder));
  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetMarketBoosterLeaderboard',
    routeTemplate: '/v1/markets/{pubkey}/boosters/leaderboard',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetMarketBoosterLeaderboardRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
    _sort: { by: args.sortBy ?? null, order: args.sortOrder ?? null },
  };
}


const _useGetMarketBoosterLeaderboardFactory = Object.assign(
  (args: useGetMarketBoosterLeaderboardArgs) => ({
    queryKey: ['markets', 'list', args.pubkey, { sortBy: args.sortBy ?? null, sortOrder: args.sortOrder ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMarketBoosterLeaderboardQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetMarketBoosterLeaderboard',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['markets', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMarketBoosterLeaderboardArgs, PagedData<GetMarketBoosterLeaderboardRow>>;


export const useGetMarketBoosterLeaderboardSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/{pubkey}/boosters/leaderboard',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetMarketBoosterLeaderboardFactory },
  parseInput: (raw: unknown): useGetMarketBoosterLeaderboardArgs => raw as useGetMarketBoosterLeaderboardArgs,
  parseResponse: (raw: unknown): PagedData<GetMarketBoosterLeaderboardRow> => raw as PagedData<GetMarketBoosterLeaderboardRow>,
  specName: 'useGetMarketBoosterLeaderboard' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['participation', 'entity', id] as readonly unknown[],
  entityProjection: {"resource":"participation","indexFields":["rank"]} as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetMarketBoosterLeaderboardRow>>;
export const useGetMarketBoosterLeaderboardPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetMarketBoosterLeaderboardArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetMarketBoosterLeaderboardQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;


// ─── useGetMarketBoosterLeaderboard · live re-rank ──────────────────────────────────────────────

/** Sort-key value → { row field, comparator } — from `sort.keys`. */
const _useGetMarketBoosterLeaderboardSortKeys: Record<
  string,
  { readonly field: string; readonly cmp: (a: unknown, b: unknown) => number }
> = {
  'amount': { field: 'totalAmount', cmp: _cmpD6 },
  'boosts': { field: 'numBoosts', cmp: _cmpNum },
  'lastBoost': { field: 'lastBoostAt', cmp: _cmpStr },
};

/**
 * Pure client-side re-sort of the loaded `GetMarketBoosterLeaderboardRow` rows by the active
 * sort key. `by` indexes `_useGetMarketBoosterLeaderboardSortKeys`; `order` is `asc`/`desc`
 * (null ⇒ `desc`). Ties break on `participationId` ascending. Returns
 * the input array unchanged when `by` is null / unknown.
 */
function resortGetMarketBoosterLeaderboardRows(
  rows: readonly GetMarketBoosterLeaderboardRow[],
  by: string | null,
  order: string | null,
): readonly GetMarketBoosterLeaderboardRow[] {
  if (by === null) return rows;
  const _k = _useGetMarketBoosterLeaderboardSortKeys[by];
  if (_k === undefined) return rows;
  const _dir = order === 'asc' ? 1 : -1;
  const _out = rows.slice();
  _out.sort((a, b) => {
    const _c = _k.cmp(
      (a as unknown as Record<string, unknown>)[_k.field],
      (b as unknown as Record<string, unknown>)[_k.field],
    );
    if (_c !== 0) return _c * _dir;
    return _cmpStr(
      (a as unknown as Record<string, unknown>)['participationId'],
      (b as unknown as Record<string, unknown>)['participationId'],
    );
  });
  return _out;
}

/**
 * Envelope wrapper — re-ranks `env.data` by `env._sort` and preserves
 * `_sort` on the result. The realtime reducer routes every row-patch
 * return through this so a live list never drifts out of sort order.
 */
function _useGetMarketBoosterLeaderboardResort(
  env: PagedData<GetMarketBoosterLeaderboardRow>,
): PagedData<GetMarketBoosterLeaderboardRow> {
  const _by = env._sort?.by ?? null;
  if (_by === null) return env;
  const _resorted = resortGetMarketBoosterLeaderboardRows(env.data, _by, env._sort?.order ?? null);
  return _resorted === env.data ? env : { ...env, data: _resorted };
}


function _useGetMarketBoosterLeaderboardPerEntitySubjects(args: useGetMarketBoosterLeaderboardArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.boost.v0`];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetMarketBoosterLeaderboardRowSubjectTemplates(args: useGetMarketBoosterLeaderboardArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [`morfi.market.${args.pubkey}.user.*.boost.v0`];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetMarketBoosterLeaderboardReconcileAlwaysSubjects(args: useGetMarketBoosterLeaderboardArgs): readonly string[] {
  const _subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.boost.v0`];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}
function _useGetMarketBoosterLeaderboardApplyEvent(
  prev: PagedData<GetMarketBoosterLeaderboardRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetMarketBoosterLeaderboardArgs,
): PagedData<GetMarketBoosterLeaderboardRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // morfi.market.${m}.user.*.boost.v0  (list-wide — subject is the route scope)
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.boost\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBoostSchema, _d)
            : fromBinary(MarketBoostSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBoostSchema>>;
      let _changed = false;
      const nextRows = prevRows.slice();
      for (let _i = 0; _i < nextRows.length; _i += 1) {
        const _rowOrig = nextRows[_i] as GetMarketBoosterLeaderboardRow;
        let updated: GetMarketBoosterLeaderboardRow = _rowOrig;
        updated = (HELPERS.applyBoostToLeaderboardRow as unknown as (prev: GetMarketBoosterLeaderboardRow, raw: ReturnType<typeof fromBinary<typeof MarketBoostSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetMarketBoosterLeaderboardRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
        if (updated !== _rowOrig) {
          nextRows[_i] = updated;
          _changed = true;
        }
      }
      if (!_changed) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _useGetMarketBoosterLeaderboardResort({ ...prev, data: nextRows });
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetMarketBoosterLeaderboardRealtime = {
  domain: 'markets' as const,
  domainPins: ['morfi.market.*.user.*.boost.v0'] as readonly string[],
  rowKey: 'participationId' as const,
  perEntitySubjects: (args: useGetMarketBoosterLeaderboardArgs) => _useGetMarketBoosterLeaderboardPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetMarketBoosterLeaderboardArgs) => _useGetMarketBoosterLeaderboardRowSubjectTemplates(args),
  rerank: (prev: PagedData<GetMarketBoosterLeaderboardRow>): PagedData<GetMarketBoosterLeaderboardRow> => _useGetMarketBoosterLeaderboardResort(prev),
  reconcileAlwaysSubjects: (args: useGetMarketBoosterLeaderboardArgs) => _useGetMarketBoosterLeaderboardReconcileAlwaysSubjects(args),
  viewportEntityArg: 'pubkey' as const,
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetMarketBoosterLeaderboardArgs) =>
    (prev: PagedData<GetMarketBoosterLeaderboardRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetMarketBoosterLeaderboardRow> | undefined =>
      _useGetMarketBoosterLeaderboardApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetMarketBoosterLeaderboardRow>, unknown>;


// ─── useGetMarketChainMetadata ────────────────────────────────────────────────────────────

export interface useGetMarketChainMetadataArgs { readonly pubkey: string; readonly fields?: readonly ('marketPubkey' | 'marketConfig' | 'quoteMint' | 'eventSchedule' | 'outcomeCount' | 'creator' | 'status')[]; }


async function _useGetMarketChainMetadataQueryFn(
  args: useGetMarketChainMetadataArgs,
  ctx: QueryFunctionContext,
): Promise<GetMarketChainMetadataRow | null> {
  if (args.pubkey === undefined || args.pubkey === null || args.pubkey === '') return null;
  const url = `/v1/markets/${args.pubkey}/chain-metadata`;
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetMarketChainMetadata',
    routeTemplate: '/v1/markets/{pubkey}/chain-metadata',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetMarketChainMetadataRow;
}


const _useGetMarketChainMetadataFactory = Object.assign(
  (args: useGetMarketChainMetadataArgs) => ({
    queryKey: ['markets', 'detail', args.pubkey, 'chain-metadata'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMarketChainMetadataQueryFn(args, ctx),
    staleTime: 10000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetMarketChainMetadata',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['markets', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMarketChainMetadataArgs, GetMarketChainMetadataRow>;


export const useGetMarketChainMetadataSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/{pubkey}/chain-metadata',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetMarketChainMetadataFactory },
  parseInput: (raw: unknown): useGetMarketChainMetadataArgs => raw as useGetMarketChainMetadataArgs,
  parseResponse: (raw: unknown): GetMarketChainMetadataRow => raw as GetMarketChainMetadataRow,
  specName: 'useGetMarketChainMetadata' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetMarketChainMetadataRow>;


// ─── useGetOutcomeSuggestions ────────────────────────────────────────────────────────────

export interface useGetOutcomeSuggestionsArgs { readonly pubkey: string; readonly viewportGated?: boolean; readonly fields?: readonly ('id' | 'marketPubkey' | 'proposalPubkey' | 'suggesterPubkey' | 'suggester' | 'label' | 'ticker' | 'imageUrl' | 'reason' | 'upvotes' | 'downvotes' | 'score' | 'status' | 'generationMode' | 'creatorEditedLabel' | 'creatorEditedTicker' | 'creatorEditedImageUrl' | 'promotedOutcomePubkey' | 'outcomeIndex' | 'promotedAt' | 'createdAt' | 'viewerKind')[]; }


async function _useGetOutcomeSuggestionsQueryFn(
  args: useGetOutcomeSuggestionsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetOutcomeSuggestionsRow>> {
  const url = `/v1/markets/${args.pubkey}/outcome-suggestions`;
  const params = new URLSearchParams();

  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetOutcomeSuggestions',
    routeTemplate: '/v1/markets/{pubkey}/outcome-suggestions',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetOutcomeSuggestionsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetOutcomeSuggestionsFactory = Object.assign(
  (args: useGetOutcomeSuggestionsArgs) => ({
    queryKey: ['markets', 'list', args.pubkey, ((args.fields) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetOutcomeSuggestionsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetOutcomeSuggestions',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['markets', 'list', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetOutcomeSuggestionsArgs, PagedData<GetOutcomeSuggestionsRow>>;


export const useGetOutcomeSuggestionsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/markets/{pubkey}/outcome-suggestions',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetOutcomeSuggestionsFactory },
  parseInput: (raw: unknown): useGetOutcomeSuggestionsArgs => raw as useGetOutcomeSuggestionsArgs,
  parseResponse: (raw: unknown): PagedData<GetOutcomeSuggestionsRow> => raw as PagedData<GetOutcomeSuggestionsRow>,
  specName: 'useGetOutcomeSuggestions' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetOutcomeSuggestionsRow>>;
export const useGetOutcomeSuggestionsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetOutcomeSuggestionsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetOutcomeSuggestionsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetOutcomeSuggestionsPerEntitySubjects(args: useGetOutcomeSuggestionsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [`morfi.market.${args.pubkey}.outcome_suggestion.v0`, `morfi.market.${args.pubkey}.outcome_suggestion_voted.v0`, `morfi.market.${args.pubkey}.outcome_added.v0`];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetOutcomeSuggestionsRowSubjectTemplates(args: useGetOutcomeSuggestionsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [`morfi.market.${args.pubkey}.outcome_suggestion.v0`, `morfi.market.${args.pubkey}.outcome_suggestion_voted.v0`, `morfi.market.${args.pubkey}.outcome_added.v0`];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetOutcomeSuggestionsApplyEvent(
  prev: PagedData<GetOutcomeSuggestionsRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetOutcomeSuggestionsArgs,
): PagedData<GetOutcomeSuggestionsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // prepend · morfi.market.${m}.outcome_suggestion.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.outcome_suggestion\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(OutcomeSuggestionEventSchema, _d)
            : fromBinary(OutcomeSuggestionEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof OutcomeSuggestionEventSchema>>;
      const _newRow = HELPERS.buildOutcomeSuggestionRowFromEvent(raw, subject, args as unknown as Record<string, unknown>) as unknown as GetOutcomeSuggestionsRow;
      const _rowId = (_newRow as unknown as Record<string, unknown>)['id'];
      // Helpers signal "this event isn't for this cache slice" by
      // returning a row whose primary key is the empty string — codegen
      // bails here so we don't prepend/replace into the wrong list.
      if (typeof _rowId !== 'string' || _rowId.length === 0) return prev;
      // Prepend-if-new, replace-if-present. A later event on the SAME
      // row id (e.g. a GiftClaimed for a previously-CREATED gift, or a
      // thesis EDIT after POSTED) rebuilds the row via the helper and
      // patches it IN PLACE so the cached row reflects post-state
      // without a refetch. Order is preserved (no re-prepend, no
      // duplicate).
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        if ((prevRows[_i] as unknown as Record<string, unknown>)['id'] === _rowId) {
          const _patched = prevRows.slice();
          _patched[_i] = _newRow;
          if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
          return { ...prev, data: _patched };
        }
      }
      // Pagination may have deliberately loaded more than the default live
      // cap. Preserve that depth and evict only one oldest row per prepend;
      // otherwise the first realtime event truncates a complete chart/feed.
      const _retainedRows = Math.max(200, prevRows.length);
      const _capped =
        prevRows.length + 1 > _retainedRows
          ? [_newRow, ...prevRows.slice(0, _retainedRows - 1)]
          : [_newRow, ...prevRows];
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: _capped };
    }
  }

  // morfi.market.${m}.outcome_suggestion_voted.v0  (list-wide — subject is the route scope)
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.outcome_suggestion_voted\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(OutcomeSuggestionVoteUpdateSchema, _d)
            : fromBinary(OutcomeSuggestionVoteUpdateSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof OutcomeSuggestionVoteUpdateSchema>>;
      let _changed = false;
      const nextRows = prevRows.slice();
      for (let _i = 0; _i < nextRows.length; _i += 1) {
        const _rowOrig = nextRows[_i] as GetOutcomeSuggestionsRow;
        let updated: GetOutcomeSuggestionsRow = _rowOrig;
        updated = (HELPERS.applyOutcomeSuggestionVote as unknown as (prev: GetOutcomeSuggestionsRow, raw: ReturnType<typeof fromBinary<typeof OutcomeSuggestionVoteUpdateSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetOutcomeSuggestionsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
        if (updated !== _rowOrig) {
          nextRows[_i] = updated;
          _changed = true;
        }
      }
      if (!_changed) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // morfi.market.${m}.outcome_added.v0  (list-wide — subject is the route scope)
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.outcome_added\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(OutcomeAddedSchema, _d)
            : fromBinary(OutcomeAddedSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof OutcomeAddedSchema>>;
      let _changed = false;
      const nextRows = prevRows.slice();
      for (let _i = 0; _i < nextRows.length; _i += 1) {
        const _rowOrig = nextRows[_i] as GetOutcomeSuggestionsRow;
        let updated: GetOutcomeSuggestionsRow = _rowOrig;
        updated = (HELPERS.applyOutcomePromotion as unknown as (prev: GetOutcomeSuggestionsRow, raw: ReturnType<typeof fromBinary<typeof OutcomeAddedSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetOutcomeSuggestionsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
        if (updated !== _rowOrig) {
          nextRows[_i] = updated;
          _changed = true;
        }
      }
      if (!_changed) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetOutcomeSuggestionsRealtime = {
  domain: 'markets' as const,
  domainPins: ['morfi.market.*.outcome_suggestion.v0', 'morfi.market.*.outcome_suggestion_voted.v0', 'morfi.market.*.outcome_added.v0'] as readonly string[],
  rowKey: 'id' as const,
  perEntitySubjects: (args: useGetOutcomeSuggestionsArgs) => _useGetOutcomeSuggestionsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetOutcomeSuggestionsArgs) => _useGetOutcomeSuggestionsRowSubjectTemplates(args),
  viewportEntityArg: 'pubkey' as const,
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetOutcomeSuggestionsArgs) =>
    (prev: PagedData<GetOutcomeSuggestionsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetOutcomeSuggestionsRow> | undefined =>
      _useGetOutcomeSuggestionsApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetOutcomeSuggestionsRow>, unknown>;
